import React, { useMemo, useState, useRef, useCallback, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye as EyeIcon,
  Bot as BotIcon,
  AlertTriangle as AlertTriangleIcon,
  CheckCircle2 as CheckCircle2Icon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LayoutList as LayoutListIcon,
  LayoutGrid as LayoutGridIcon,
} from 'lucide-react';

// Cast Lucide icons to avoid "ReactNode is not a valid JSX element" errors (project-wide TS config issue)
type LucideProps = { size?: number; className?: string };
const Eye = EyeIcon as React.FC<LucideProps>;
const Bot = BotIcon as React.FC<LucideProps>;
const AlertTriangle = AlertTriangleIcon as React.FC<LucideProps>;
const CheckCircle2 = CheckCircle2Icon as React.FC<LucideProps>;
const ChevronLeft = ChevronLeftIcon as React.FC<LucideProps>;
const ChevronRight = ChevronRightIcon as React.FC<LucideProps>;
const LayoutList = LayoutListIcon as React.FC<LucideProps>;
const LayoutGrid = LayoutGridIcon as React.FC<LucideProps>;
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import { DrawingCanvas } from '../../ComponnentsUI/DrawingCanvas/components/DrawingCanvas';
import { Select } from '../../ComponnentsUI/common/Select';
import type {
  ImageInput,
  DrawingCanvasRef,
} from '../../ComponnentsUI/DrawingCanvas/components/DrawingCanvas';
import type { Shape } from '../../ComponnentsUI/DrawingCanvas/types';
import {
  rawShapeToShape,
  type RawShapeData,
} from '../../ComponnentsUI/DrawingCanvas/utils/pointsConverter';
import { collectDuplicateCategoryDeleteIds, convertShapesToSegmentations, dedupeSegmentationsForLoad, enrichShapeWithMeasurementOverlay, getSegmentationStructureDedupeKey, groupStructureConflictsBySlot, resolveSegmentationCategoryId, resolveSegmentationLabelsForLoad } from '../../ComponnentsUI/DrawingCanvas/utils/segmentationSaveUtils';
import { linkAndSyncDerivedBpdShapes } from '../../ComponnentsUI/DrawingCanvas/utils/ellipseBpdUtils';
import {
  useGetInstanceFramesMetadata,
  useGetSingleFrame,
  fetchFrameImage,
  getCachedFrameImageUrl,
  removeCachedFrameImageUrl,
  getCachedImageBitmap,
  clearGeneralViewerFrameCache,
  getVideoStreamUrl,
  triggerVideoThumbnailWarmup,
  useSaveSegmentations,
  useAssignUserView,
  useValidateFrameForView,
  useGetViewDefinitions,
  type ViewAgreement,
  type ViewValidationResult,
} from '../../../helpers/api/view_frame/view-frames';
import { apiClient } from '../../../helpers/apiHelper';
import { useCategories } from '../../../contexts/CategoriesContext';
import { useFilterOptions } from '../../../contexts/FilterOptionsContext';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';
import type { LiveViewerExportHandlers } from './ViewerToolCluster';
import CollaborationDiscussButton from '../Collaboration/CollaborationDiscussButton';
import { buildGeneralViewerFrameTarget } from '../Collaboration/collaboration-types';
import { useCollaborationViewerLocation } from '../Collaboration/CollaborationContext';
import { runWithIndeterminateExportNotification } from './exportProgressNotification';
import {
  downloadBlobFile,
  downloadDicomInstance,
  downloadFrameJpegFromUrl,
} from './viewerExportUtils';
import {
  planeConfigsCriteria,
} from '../../../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';
import { buildViewMeasurementKeysFromViewDef } from '../../../helpers/api/config/config';

// ── Segmentation ↔ DrawingCanvas conversion (exported for reuse e.g. Quick Inference) ──

export interface SegmentationLike {
  id?: number | string;
  category_id?: number | null;
  label?: string | null;
  geometry_type?: string;
  type?: string;
  segment_type?: string | null;
  points?: string;
  length?: number | null;
  perimeter?: number | null;
  area?: number | null;
  score?: number;
  is_manual?: boolean;
}

export interface SegmentationOutput {
  id?: number | string;
  category_id?: number;
  label?: string | null;
  geometry_type: string;
  type?: string;
  segment_type: 'structure' | 'measurement';
  points: string;
  length?: number;
  perimeter?: number;
  area?: number;
  score?: number;
  is_manual?: boolean;
}



export function getSegGeometryType(seg: { geometry_type?: string; type?: string }): string {
  const v = (seg?.geometry_type ?? seg?.type ?? 'FREEHAND').toString().trim().toUpperCase();
  return v || 'FREEHAND';
}

export function backendLabelToDisplayName(label: string): string {
  const cleaned = label
    .replace(/_?not_?visible$/gi, '')
    .replace(/\s*\(not\s*visible\)\s*/gi, '')
    .replace(/_+$/, '')
    .trim();
  return cleaned
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function convertSegmentationToRawShape(
  segmentation: SegmentationLike,
  frameKey: string
): RawShapeData | null {
  if (!segmentation || !segmentation.points) return null;
  try {
    const geoType = getSegGeometryType(segmentation);
    const coords = segmentation.points
      .split(/[,]|[\\]/g)
      .map((c: string) => parseFloat(c.trim()))
      .filter((c: number) => !isNaN(c));
    if (coords.length < 2) return null;

    let shapeType: 'line' | 'square' | 'circle' | 'freehand' = 'freehand';
    let pointsString = '';
    if (geoType === 'ELLIPSE') {
      shapeType = 'circle';
      if (coords.length >= 8) {
        // 4 rotated axis-tip points: p0/p1 = major axis endpoints, p2/p3 = minor axis endpoints
        const p0x = coords[0], p0y = coords[1];
        const p1x = coords[2], p1y = coords[3];
        const p2x = coords[4], p2y = coords[5];
        const centerX = (p0x + p1x) / 2;
        const centerY = (p0y + p1y) / 2;
        const radiusX = Math.sqrt((p0x - centerX) ** 2 + (p0y - centerY) ** 2);
        const radiusY = Math.sqrt((p2x - centerX) ** 2 + (p2y - centerY) ** 2);
        let rotation = Math.atan2(p0y - centerY, p0x - centerX);
        if (rotation > Math.PI / 2) rotation -= Math.PI;
        if (rotation < -Math.PI / 2) rotation += Math.PI;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        // Encode center + semi-major/minor axis tips (matches shapeToRawShape / rawShapeToShape)
        pointsString = `${centerX}//${centerY},${centerX + radiusX * cos}//${centerY + radiusX * sin},${centerX - radiusY * sin}//${centerY + radiusY * cos}`;
      } else if (coords.length >= 6) {
        // 3 points (center, semi-major end, semi-minor end) — batch/realtime cv2.fitEllipse encoding
        pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]},${coords[4]}//${coords[5]}`;
      } else {
        pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]}`;
      }
    } else if (geoType === 'LINE' || geoType === 'POLYLINE') {
      shapeType = 'line';
      pointsString = `${coords[0]}//${coords[1]},${coords[2]}//${coords[3]}`;
    } else if (geoType === 'POLYGON' && (coords.length === 8 || segmentation.label === 'meas_rect')) {
      const xCoords = coords.filter((_: number, i: number) => i % 2 === 0);
      const yCoords = coords.filter((_: number, i: number) => i % 2 !== 0);
      shapeType = 'square';
      pointsString = `${Math.min(...xCoords)}//${Math.min(...yCoords)},${Math.max(...xCoords)}//${Math.max(...yCoords)}`;
    } else {
      const pairs: string[] = [];
      for (let i = 0; i < coords.length; i += 2) {
        pairs.push(`${coords[i]}//${coords[i + 1]}`);
      }
      pointsString = pairs.join(',');
    }

    const resolvedLabel = segmentation.label ?? null;
    const displayLabel = resolvedLabel
      ? backendLabelToDisplayName(resolvedLabel)
      : (segmentation.category_id != null ? `Category ${segmentation.category_id}` : 'Structure');
    const segmentType = (segmentation.segment_type ??
      (resolvedLabel?.startsWith('MEAS__') ? 'measurement' : 'structure')) as 'structure' | 'measurement';

    const segId = segmentation.id ?? `tmp_${frameKey}_${Math.random().toString(36).slice(2)}`;

    return {
      id: `seg_${frameKey}_${segId}`,
      type: shapeType,
      points: pointsString,
      label: displayLabel,
      dbId: segId,
      backendLabel: resolvedLabel,
      backendType: geoType,
      categoryId: segmentation.category_id ?? undefined,
      segmentType,
    };
  } catch {
    return null;
  }
}

/** Ensure every segmentation has a stable id for DrawingCanvas tracking. */
export function ensureSegmentationIds(
  segmentations: SegmentationLike[]
): SegmentationLike[] {
  return segmentations.map((seg, i) => ({
    ...seg,
    id: seg.id ?? `qi_${i}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  }));
}

function normalizeQualFlagCode(raw: string): string {
  return String(raw ?? '')
    .replace(/^QUAL_FLAG__/, '')
    .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

interface GeneralViewerInstanceDetailProps {
  studyUID: string;
  instance: GeneralViewerInstance;
  filters?: { viewCodes?: string[]; qualityFlagCodes?: string[]; categoryGroups?: string[] };
  onBack: () => void;
  /** Called once the filtered frame list is known so the parent can show the correct count on the instance card. */
  onFrameCountLoaded?: (sopInstanceUID: string, count: number) => void;
  /** When set, pre-select this view_code in the assignment panel so the user can draw and confirm immediately. */
  initialPendingViewCode?: string | null;
  /** Called after the user successfully confirms and saves the pre-selected view — use to navigate back to Views tab. */
  onViewConfirmedAndSaved?: () => void;
  /** 0-based index in allFramesMeta to jump to. Same cache order as the exam strip's allFrames. */
  initialFrameIdx?: number;
  /** DB primary key (frame_id) of the frame to jump to on open. Preferred over initialFrameIdx. */
  initialFrameId?: string;
  /** Pushes live export handlers to the parent top bar (ViewerToolCluster). */
  onLiveExportHandlersChange?: (handlers: LiveViewerExportHandlers | null) => void;
  /** Single-frame mode (report "Captured Views" editor): only this frame is fetched (via the
   *  single-frame endpoint) — no instance-wide metadata or other frame images are requested. */
  restrictToFrameId?: string;
  /** With restrictToFrameId: view_def_id used by the backend to scope the single frame's
   *  segmentations/quality flags to the report view being explained. */
  restrictToViewDefId?: number | null;
  /** With restrictToFrameId: only load segmentations belonging to this view (its structure
   *  categories + its MEAS__ keys from planes.config). Other segmentations on the same image
   *  are hidden — used by the report explainability editor. */
  segmentationViewCode?: string | null;
  /** External prev/next navigation (e.g. cycle through the report's captured views instead of
   *  DICOM frames). Drives DrawingCanvas's global nav: prev/next buttons, slider and jump input. */
  externalNav?: {
    total: number;
    /** 0-based index of the currently open item. */
    currentIndex: number;
    /** Called with the 0-based index of the item to open. */
    onNavigate: (index: number) => void;
  };
  /**
   * Read-only mode for the report explainability viewers: hides the view-assignment /
   * confirmation panel and disables the DrawingCanvas editing tools and save. Playback and
   * segmentation viewing stay enabled. (Editing/assignment from these modals is handled later.)
   */
  readOnly?: boolean;
  /**
   * Smart/lazy image fetching for large full-instance clips (report finding explainability).
   *
   * When enabled, the eager "download every frame up front" worker pool is disabled. Instead:
   *   1. On open, only the {@link priorityFrameIds} (the frames that prove the finding) and the
   *      current frame are fetched — a handful of requests instead of hundreds.
   *   2. When the user presses play, frames are streamed sequentially from the current position
   *      to the end of the clip, then the remaining head (0 → current) is back-filled.
   *   3. This session's blob URLs are revoked (and evicted from the module cache) on unmount, so
   *      opening/closing many findings keeps memory bounded.
   *
   * Navigation to any single frame still triggers an on-demand priority fetch, so scrubbing works.
   */
  lazyImageFetch?: boolean;
  /**
   * With {@link lazyImageFetch}: the `frame_id`s to prefetch immediately on open (the proving
   * frames). Order is preserved so the first proving frame loads first.
   */
  priorityFrameIds?: string[];
}

// ── Transparent 1×1 JPEG placeholder shown while images are loading ─────────
const PLACEHOLDER_SRC =
  'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/wAARC AABAAEDASIAAhEBAxEB/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AJQAB/9k=';

/** Only convert segmentations→shapes for frames in [current − N, current + N]. */
const SHAPES_WINDOW = 40;
/** When we have more than this many frames, only keep a window in the canvas list so it stays fast. */
const FRAME_LIST_WINDOW_HALF = 30;
const FRAME_LIST_WINDOW_THRESHOLD = FRAME_LIST_WINDOW_HALF * 2 + 1;
/** Small SVG icon that visually represents a grid with `cols` columns × `rows` rows. */
function GridLayoutIcon({ cols, rows = 2, size = 14 }: { cols: number; rows?: number; size?: number }) {
  const gap = 1;
  const cellW = (size - (cols - 1) * gap) / cols;
  const cellH = (size - (rows - 1) * gap) / rows;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      {Array.from({ length: rows }, (_, row) =>
        Array.from({ length: cols }, (_, col) => (
          <rect
            key={`${row}-${col}`}
            x={col * (cellW + gap)}
            y={row * (cellH + gap)}
            width={cellW}
            height={cellH}
            rx={0.5}
            fill="currentColor"
          />
        ))
      )}
    </svg>
  );
}

/** Number of parallel workers in the background DICOM frame fetch pool (single-mode only).
 *  Matches typical HTTP/2 multiplexing; large enough to saturate the pipeline without overwhelming the server. */
const CONCURRENT_FETCHES = 8;

/** Lazy explainability playback: how many frames ahead of the current position to keep prefetched
 *  while playing. A sliding window (instead of "download the whole clip") keeps concurrent load low
 *  so the backend isn't flooded, while staying far enough ahead that buffered playback rarely stalls. */
const LAZY_PLAY_PREFETCH_AHEAD = 16;
/** Lazy explainability playback: parallel fetches for the sliding window. Kept small on purpose to
 *  avoid the request storm / 500s seen when every frame was requested at once. */
const LAZY_PLAY_CONCURRENT = 4;
/** Lazy buffered playback: transient fetch failures (e.g. 500) are retried this many times before
 *  the frame is skipped, so a blip doesn't permanently blank a frame nor stall playback forever. */
const LAZY_PLAY_MAX_RETRIES = 3;

/** Max parallel thumbnail fetches for VIDEO instances in grid mode.
 *  Video thumbnails are generated on-the-fly by the server (expensive CPU/IO), so we keep
 *  this intentionally small to avoid overwhelming the backend and causing 20–30 s delays. */
const VIDEO_THUMB_CONCURRENT = 3;

/** Max parallel thumbnail fetches for DICOM instances in grid mode.
 *  Higher than video because DICOM thumbnails are pre-rendered (fast), but still capped to
 *  avoid flooding the server and accumulating hundreds of blobs simultaneously. */
const DICOM_GRID_CONCURRENT = 6;

/** Maximum number of grid thumbnail blobs kept in memory at once.
 *  Must be ≤ MAX_BLOB_CACHE_ENTRIES in view-frames.ts so the module cache never revokes
 *  a URL the component is still rendering (which would cause broken images on scroll-back).
 *  At ~50 KB/blob × 3000 ≈ 150 MB — fine alongside the 200-entry ImageBitmap cap. */
const MAX_GRID_BLOBS = 3000;

function GeneralViewerInstanceDetailInner({
  studyUID,
  instance,
  filters,
  onBack,
  onFrameCountLoaded,
  initialPendingViewCode,
  onViewConfirmedAndSaved,
  initialFrameIdx,
  initialFrameId,
  onLiveExportHandlersChange,
  restrictToFrameId,
  restrictToViewDefId,
  segmentationViewCode,
  externalNav,
  readOnly = false,
  lazyImageFetch = false,
  priorityFrameIds,
}: GeneralViewerInstanceDetailProps) {
  const { t } = useTranslation('GeneralViewer');
  const [isSaving, setIsSaving] = useState(false);
  const drawingCanvasRef = useRef<DrawingCanvasRef>(null);
  const [currentImageId, setCurrentImageId] = useState<string | undefined>(undefined);
  // Track whether we already applied the initialFrameId jump so we don't repeat it
  const initialFrameAppliedRef = useRef(false);
  // Synchronous render-time update — NEVER use an effect for this ref.
  // The rAF effect runs before any effect that updates this ref, so the effect order would
  // cause a stale read. Updating during render guarantees the rAF effect always sees the
  // current value of currentImageId (e.g. first frame after restart).
  const currentImageIdRef = useRef<string | undefined>(undefined);
  currentImageIdRef.current = currentImageId;

  const { categories, getColorForCategoryId, getColorForCode, codeById, categoryIdToMeasLabel, categoryIdsWithMaxTwo, measLabelToCategoryId, labelToMeasName, measGeometryContext, categoryIdToMeasNames } = useCategories();
  const filterOptions = useFilterOptions();
  const { assignUserView } = useAssignUserView();
  const { validate: validateFrameForView } = useValidateFrameForView();
  const { saveSegmentations } = useSaveSegmentations();
  const [isAssigningView, setIsAssigningView] = useState(false);
  const [pendingViewDefId, setPendingViewDefId] = useState<number | null>(null);
  // Ref mirror so handleSave (non-memoized) always reads the current pendingViewDefId without stale closure
  const pendingViewDefIdRef = useRef<number | null>(null);
  pendingViewDefIdRef.current = pendingViewDefId;

  // ── Resolve initialPendingViewCode → numeric view_def_id and pre-select it ─
  const { data: viewDefsData } = useGetViewDefinitions();
  const resolvedInitialViewDefId = useMemo(() => {
    if (!initialPendingViewCode) return null;
    const defs: any[] = viewDefsData?.data?.data ?? viewDefsData?.data ?? [];
    if (!Array.isArray(defs)) return null;
    const match = defs.find(
      (d: any) => d.view_code === initialPendingViewCode || d.view_code === `VIEW__${initialPendingViewCode}`
    );
    return match?.id ?? null;
  }, [initialPendingViewCode, viewDefsData]);

  // Ref used by the assign-mode validation effect defined later in the component.
  // We store resolvedInitialViewDefId in a ref so the later effect can always read
  // the latest value without it needing to be in its dependency array.
  const resolvedInitialViewDefIdRef = useRef<number | null>(null);
  resolvedInitialViewDefIdRef.current = resolvedInitialViewDefId;
  const [syncShapesVersion, setSyncShapesVersion] = useState(0);
  const [validation, setValidation] = useState<ViewValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSavingToReport, setIsSavingToReport] = useState(false);
  // Ref to track last auto-validated frame+view pair so we don't re-run on every render
  const lastAutoValidatedKey = useRef<string | null>(null);

  // ── 1. Fetch frame metadata ──────────────────────────────────────────────────
  // Normal mode: ALL frame metadata of the instance in one lightweight request.
  // Single-frame mode (report editor): ONLY the clicked frame via the single-frame
  // endpoint — one request returning metadata + segmentations + the image itself
  // (base64), so no instance-wide metadata and no per-frame image requests are made.
  const isSingleFrameMode = !!restrictToFrameId;
  const {
    data: metaResponse,
    isLoading: isAllMetaLoading,
    refetch: refetchAllMeta,
  } = useGetInstanceFramesMetadata(studyUID, instance.sop_instance_uid, filters, {
    enabled: !isSingleFrameMode,
  });
  const {
    data: singleFrameResponse,
    isLoading: isSingleFrameLoading,
    refetch: refetchSingleFrame,
  } = useGetSingleFrame(restrictToFrameId ?? null, studyUID, {
    enabled: isSingleFrameMode,
    viewDefId: restrictToViewDefId ?? null,
  });
  const isMetaLoading = isSingleFrameMode ? isSingleFrameLoading : isAllMetaLoading;
  const refetchMeta = isSingleFrameMode ? refetchSingleFrame : refetchAllMeta;

  // ── Per-view segmentation filter (single-frame report editor) ────────────────
  // Keep segmentations for this view's quality-rule categories and API-derived meas keys.
  const viewSegmentationPredicate = useMemo<((seg: any) => boolean) | null>(() => {
    if (!segmentationViewCode) return null;
    const codeCandidates = [segmentationViewCode, `VIEW__${segmentationViewCode}`];
    const viewDef = filterOptions.views.find((v) =>
      codeCandidates.includes(v.view_code)
    );
    const measKeys = buildViewMeasurementKeysFromViewDef(categories, viewDef);
    const configKey = codeCandidates.find(
      (code) => (planeConfigsCriteria as Record<string, any>)[code]
    );
    const criteria = configKey
      ? (planeConfigsCriteria as Record<string, any>)[configKey]?.criteria
      : undefined;
    if (!Array.isArray(criteria) && measKeys.size === 0) return null;
    const categoryIds = new Set<number>(
      (Array.isArray(criteria) ? criteria : [])
        .map((c: any) => Number(c.label))
        .filter((n: number) => !isNaN(n))
    );
    viewDef?.quality_rules?.forEach((rule) => {
      categoryIds.add(Number(rule.category_id));
    });
    measKeys.forEach((measKey) => {
      const catId = (measLabelToCategoryId as Record<string, number>)?.[measKey];
      if (catId != null) categoryIds.add(Number(catId));
    });
    return (seg: any) => {
      const catId = resolveSegmentationCategoryId(seg, measLabelToCategoryId);
      if (catId != null && categoryIds.has(Number(catId))) return true;
      const label = (seg?.label ?? '').toString().trim();
      if (label) {
        const measKey = label.split(/\s+/)[0];
        if (measKeys.has(measKey)) return true;
      }
      return false;
    };
  }, [segmentationViewCode, measLabelToCategoryId, filterOptions.views, categories]);

  const allFramesMeta: any[] = useMemo(() => {
    let frames: any[];
    if (isSingleFrameMode) {
      // Map the single-frame response onto the same shape the metadata endpoint
      // returns, so the whole component works unchanged with a one-entry list.
      const frame = singleFrameResponse?.data?.data ?? singleFrameResponse?.data;
      frames = frame?.frame_id
        ? [
            {
              ...frame,
              frame_ml_view_def_ids:
                frame.frame_ml_view_def_ids ?? frame.view_def_ids ?? [],
              view_agreement: frame.view_agreement ?? 'none',
            },
          ]
        : [];
    } else {
      const data = metaResponse?.data?.data ?? metaResponse?.data;
      frames = Array.isArray(data?.frames) ? data.frames : [];
    }
    if (viewSegmentationPredicate) {
      frames = frames.map((f: any) =>
        Array.isArray(f.segmentations)
          ? { ...f, segmentations: f.segmentations.filter(viewSegmentationPredicate) }
          : f
      );
    }
    return frames;
  }, [metaResponse, singleFrameResponse, isSingleFrameMode, viewSegmentationPredicate]);

  const allFramesMetaRef = useRef(allFramesMeta);
  allFramesMetaRef.current = allFramesMeta;

  // Notify parent of the (filtered) frame count so the instance card stays in sync
  useEffect(() => {
    if (!isMetaLoading && onFrameCountLoaded) {
      onFrameCountLoaded(instance.sop_instance_uid, allFramesMeta.length);
    }
  }, [allFramesMeta.length, isMetaLoading, instance.sop_instance_uid, onFrameCountLoaded]);

  // ── 2. DICOM image loading (pure-ref, no state for blobs) ────────────────────
  // `blobUrlsRef` holds all loaded blobs; React state is NOT used for individual blobs.
  // This prevents a re-render on every blob arriving, which caused the infinite-render loop:
  //   blobUrls state changes → imagesForDrawingCanvas recomputes → DrawingCanvas re-renders →
  //   onImageChange fires → setCurrentImageId → re-render → repeat.
  //
  // Instead we have:
  //   • `currentFrameBlobVersion`  – incremented (once) when the CURRENTLY DISPLAYED frame's
  //     blob finishes loading. This is the ONLY signal DrawingCanvas needs to redraw.
  //   • `loadedBlobCount`          – accumulated counter for the loading-progress indicator,
  //     flushed every 200 ms so the spinner updates without causing expensive memo recomputes.
  const blobUrlsRef = useRef<Record<string, string>>({});
  const loadingRef = useRef<Set<string>>(new Set());
  const failedFrameKeysRef = useRef<Set<string>>(new Set());
  const [currentFrameBlobVersion, setCurrentFrameBlobVersion] = useState(0);
  const [loadedBlobCount, setLoadedBlobCount] = useState(0);
  const pendingLoadCountRef = useRef(0); // frames loaded since last flush
  const abortControllerRef = useRef<AbortController | null>(null);
  /** Ref to the grid scroll container — used by the IntersectionObserver for lazy loading (DICOM and video). */
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  /** Persisted IntersectionObserver so new-page cells can be registered without recreating the whole observer. */
  const gridObserverRef = useRef<IntersectionObserver | null>(null);
  /** Cache each grid tile's natural image size so we can draw segmentations in correct coordinates. */
  const gridImageDimsRef = useRef<Record<string, { w: number; h: number }>>({});
  const [gridOverlayVersion, setGridOverlayVersion] = useState(0);
  /** LRU order for grid blobs: oldest at index 0, most-recently-viewed at end.
   *  Used to evict the least-recently-viewed frame when memory cap is reached. */
  const blobLruRef = useRef<string[]>([]);
  /** Maps frameKey → image_url so we can remove module-level cache entries on LRU eviction. */
  const blobKeyToImageUrlRef = useRef<Map<string, string>>(new Map());
  /** lazyImageFetch: frameKey → image_url for every blob this session fetched, so we can
   *  revoke + evict exactly what we created when the modal closes (bounded memory). */
  const lazySessionRef = useRef<Map<string, string>>(new Map());
  /** lazy buffered playback: per-frame transient-failure retry counter (frameKey → attempts). */
  const lazyRetryRef = useRef<Map<string, number>>(new Map());

  const isVideoInstance = instance.format_file === 'VIDEO';

  // Playback state — declared early because several effects below gate on isPlaying/playFps
  const [isPlaying, setIsPlaying] = useState(false);
  const [playFps, setPlayFps] = useState(4);
  const [frameViewMode, setFrameViewMode] = useState<'single' | 'grid'>('single');
  // NxN grid: gridSize = N means N cols × N rows per page
  const [gridSize, setGridSize] = useState<number>(3);
  const [gridPage, setGridPage] = useState<number>(0);
  const [showGridPicker, setShowGridPicker] = useState(false);
  const [gridHoverN, setGridHoverN] = useState(0);

  // When entering grid mode, open the page that contains the current frame (so we don't restart at 1).
  useEffect(() => {
    if (frameViewMode !== 'grid') return;
    const perPage = gridSize * gridSize;
    if (perPage <= 0 || allFramesMeta.length === 0) return;
    const idx0 =
      currentImageId != null
        ? allFramesMeta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId)
        : 0;
    const safeIdx0 = idx0 >= 0 ? idx0 : 0;
    const desired = Math.floor(safeIdx0 / perPage);
    setGridPage((prev) => (prev === desired ? prev : desired));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameViewMode, gridSize, currentImageId, allFramesMeta.length]);
  const gridPickerRef = useRef<HTMLDivElement | null>(null);

  // VIDEO: display is the live <video> element (no frame capture); frame is derived from video.currentTime (original FPS).
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const [videoStreamData, setVideoStreamData] = useState<{ url: string; fps: number; total_frames: number } | null>(null);
  const [videoStreamFetchFailed, setVideoStreamFetchFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  /** True when <video> fired error (e.g. 403 URL, unsupported codec). Distinct from URL API failure. */
  const [videoElementFailed, setVideoElementFailed] = useState(false);
  /** Video intrinsic size for canvas coordinate mapping when drawing over the video. */
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const videoPlaybackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Close grid picker on outside click
  useEffect(() => {
    if (!showGridPicker) return;
    const handler = (e: MouseEvent) => {
      if (gridPickerRef.current && !gridPickerRef.current.contains(e.target as Node)) {
        setShowGridPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGridPicker]);

  // On instance change: abort in-flight fetches and reset all DICOM blob state.
  useEffect(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    clearGeneralViewerFrameCache();
    blobUrlsRef.current = {};
    loadingRef.current.clear();
    failedFrameKeysRef.current.clear();
    pendingLoadCountRef.current = 0;
    blobLruRef.current = [];
    blobKeyToImageUrlRef.current.clear();
    lazySessionRef.current.clear();
    lazyRetryRef.current.clear();
    setLoadedBlobCount(0);
    setCurrentFrameBlobVersion(0);
    setCurrentImageId(undefined);
    initialFrameAppliedRef.current = false; // allow re-applying initialFrameId on instance switch
    if (isVideoInstance) {
      if (videoPlaybackIntervalRef.current) {
        clearInterval(videoPlaybackIntervalRef.current);
        videoPlaybackIntervalRef.current = null;
      }
      setVideoStreamData(null);
      setVideoStreamFetchFailed(false);
      setVideoElementFailed(false);
      setVideoReady(false);
      setVideoDimensions(null);
      if (videoElementRef.current) {
        videoElementRef.current.pause();
        videoElementRef.current.src = '';
      }
    }
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [studyUID, instance.sop_instance_uid]);

  // When filters change the frame list changes completely. Reset currentImageId so
  // the canvas jumps to frame 0 of the new filtered list instead of keeping a
  // stale ID that no longer exists in the new list (which produces "Frame 0 of N").
  const filtersKey = JSON.stringify(filters ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCurrentImageId(undefined);
  }, [filtersKey]);

  // VIDEO: fetch signed stream URL once (no per-frame server requests)
  useEffect(() => {
    if (!isVideoInstance || !instance.sop_instance_uid) return;
    let cancelled = false;
    setVideoStreamFetchFailed(false);
    getVideoStreamUrl(instance.sop_instance_uid)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setVideoStreamData(data);
        } else {
          setVideoStreamData(null);
          setVideoStreamFetchFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVideoStreamData(null);
          setVideoStreamFetchFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isVideoInstance, instance.sop_instance_uid]);

  const allFramesMetaLength = allFramesMeta.length;

  // ── Priority fetch: load the current frame immediately when it changes ───────
  // This fires when the user navigates (prev/next/slider) and the frame isn't loaded yet.
  // It runs in parallel with the background worker-pool fetch so navigating to an
  // unloaded frame shows the blob as fast as possible without waiting for the queue.
  const loadCurrentFrameNow = useCallback(async (frameKey: string) => {
    if (isVideoInstance) return;
    if (blobUrlsRef.current[frameKey] || loadingRef.current.has(frameKey) || failedFrameKeysRef.current.has(frameKey)) return;
    const frame = allFramesMetaRef.current.find((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === frameKey);
    if (!frame?.image_url) return;
    const signal = abortControllerRef.current?.signal;
    loadingRef.current.add(frameKey);
    try {
      const cached = getCachedFrameImageUrl(frame.image_url);
      const blobUrl = cached ?? await fetchFrameImage(frame.image_url, { signal });
      if (signal?.aborted) return;
      blobUrlsRef.current[frameKey] = blobUrl;
      // In lazy mode, track on-demand (prev/next/slider) fetches too so they are freed on close.
      if (lazyImageFetch) lazySessionRef.current.set(frameKey, frame.image_url);
      pendingLoadCountRef.current++;
      // If this is still the displayed frame, tell React to re-render DrawingCanvas
      if (frameKey === currentImageIdRef.current) setCurrentFrameBlobVersion((v) => v + 1);
    } catch (e: any) {
      if (e?.name !== 'AbortError') failedFrameKeysRef.current.add(frameKey);
    } finally {
      loadingRef.current.delete(frameKey);
    }
  }, [isVideoInstance, lazyImageFetch]);

  useEffect(() => {
    if (isVideoInstance || !currentImageId || allFramesMetaLength === 0) return;
    if (!blobUrlsRef.current[currentImageId]) loadCurrentFrameNow(currentImageId);
  }, [currentImageId, allFramesMetaLength, isVideoInstance, loadCurrentFrameNow]);

  // ── Single-frame mode: the image already arrived as base64 in the single-frame
  // response — decode it into a blob URL directly so no image request is needed.
  const singleFrameImageBase64: string | null = useMemo(() => {
    if (!isSingleFrameMode) return null;
    const frame = singleFrameResponse?.data?.data ?? singleFrameResponse?.data;
    return frame?.image_base64 || null;
  }, [isSingleFrameMode, singleFrameResponse]);

  useEffect(() => {
    if (!isSingleFrameMode || isVideoInstance || !singleFrameImageBase64) return;
    const frame = allFramesMetaRef.current[0];
    if (!frame) return;
    const frameKey = `${frame.sop_instance_uid}_${frame.frame_number}`;
    let blobUrl: string | null = null;
    try {
      const byteString = atob(singleFrameImageBase64);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
      const prev = blobUrlsRef.current[frameKey];
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      blobUrlsRef.current[frameKey] = blobUrl;
      pendingLoadCountRef.current++;
      setCurrentFrameBlobVersion((v) => v + 1);
    } catch {
      // Malformed base64 — leave the placeholder; nothing else to fall back to.
    }
    return () => {
      if (blobUrl && blobUrlsRef.current[frameKey] === blobUrl) {
        URL.revokeObjectURL(blobUrl);
        delete blobUrlsRef.current[frameKey];
      }
    };
    // allFramesMeta dep ensures the frame entry exists before seeding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleFrameMode, isVideoInstance, singleFrameImageBase64, allFramesMetaLength]);

  // ── Counter flush: update loading progress at most every 200 ms ──────────────
  // Only increments a counter — no blob data in state — so the re-render is tiny.
  // Runs for both DICOM and video — video instances need this to re-render the grid
  // after video-frame thumbnails are loaded into blobUrlsRef in grid mode.
  useEffect(() => {
    const id = setInterval(() => {
      const n = pendingLoadCountRef.current;
      if (n === 0) return;
      pendingLoadCountRef.current = 0;
      setLoadedBlobCount((prev) => prev + n);
    }, 200);
    return () => clearInterval(id);
  }, []);

  // ── Initial frame jump via DB primary key (frame_id) ─────────────────────────────────────────
  // Uses frame_id (unique, ordering-independent) to locate the exact frame regardless of
  // SOP-UID grouping or frame_number collisions.
  // The jump-guard pattern (jumpTargetRef + isJumpingRef) mirrors goToFrameIndex so that when
  // the virtual window shifts on this setCurrentImageId call, DrawingCanvas's echo fires with
  // images[0] of the NEW window — which the guard correctly discards until DC confirms the target.
  useEffect(() => {
    if (!initialFrameId || allFramesMetaLength === 0) return;
    const target = allFramesMetaRef.current.find(
      (f: any) => String(f.frame_id) === initialFrameId,
    );
    if (!target) return;
    const targetKey = `${target.sop_instance_uid}_${target.frame_number}`;
    jumpTargetRef.current = targetKey;
    if (jumpClearTimeoutRef.current) clearTimeout(jumpClearTimeoutRef.current);
    jumpClearTimeoutRef.current = setTimeout(() => {
      jumpTargetRef.current = null;
      jumpClearTimeoutRef.current = null;
    }, 100);
    isJumpingRef.current = true;
    requestAnimationFrame(() => { isJumpingRef.current = false; });
    setCurrentImageId(targetKey);
    if (target.image_url) {
      const jumpImageUrl = target.image_url;
      fetchFrameImage(jumpImageUrl, { signal: abortControllerRef.current?.signal ?? undefined })
        .then((blobUrl) => {
          if (blobUrl) {
            blobUrlsRef.current[targetKey] = blobUrl;
            if (lazyImageFetch) lazySessionRef.current.set(targetKey, jumpImageUrl);
            setCurrentFrameBlobVersion((v) => v + 1);
          }
        })
        .catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFrameId, allFramesMetaLength]);

  // ── Background worker-pool: fetch ALL remaining frames with CONCURRENT_FETCHES parallel workers ──
  // Worker-pool pattern: N async workers each grab the next unloaded task from a shared index.
  // This saturates the server connection limit without serial batching, and is ~CONCURRENT_FETCHES× faster.
  // In grid mode the IntersectionObserver lazy-loader handles fetching — pause this pool to avoid
  // loading thousands of off-screen frames that would exhaust memory.
  useEffect(() => {
    if (allFramesMetaLength === 0 || isVideoInstance || frameViewMode === 'grid') return;
    // Lazy mode owns fetching (see the dedicated effect below) — never eagerly download all frames.
    if (lazyImageFetch) return;
    const meta = allFramesMetaRef.current;
    const signal = abortControllerRef.current?.signal;
    if (!meta.length || signal?.aborted) return;

    // Jump to a specific frame when navigating from the exam list strip.
    // initialFrameIdx is the 0-based index in allFramesMeta (same shared cache order as the strip).
    // Using the index directly avoids any frame_number conversion or ordering mismatch.
    if (initialFrameIdx !== undefined && initialFrameIdx !== null && !initialFrameAppliedRef.current) {
      const target = meta[initialFrameIdx] ?? meta[0];
      initialFrameAppliedRef.current = true;
      const targetKey = `${target.sop_instance_uid}_${target.frame_number}`;
      setCurrentImageId(targetKey);
      // Priority-load the target frame immediately so it shows instantly
      if (target.image_url) {
        fetchFrameImage(target.image_url, { signal: abortControllerRef.current?.signal ?? undefined })
          .then((blobUrl) => {
            if (blobUrl) {
              blobUrlsRef.current[targetKey] = blobUrl;
              setCurrentFrameBlobVersion((v) => v + 1);
            }
          })
          .catch(() => { });
      }
      // Do NOT return — fall through to start the background worker pool for all other frames
    } else {
      const firstKey = `${meta[0].sop_instance_uid}_${meta[0].frame_number}`;
      setCurrentImageId((prev) => (prev == null ? firstKey : prev));
    }

    let cancelled = false;
    let taskIdx = 0;

    const worker = async () => {
      while (taskIdx < meta.length) {
        if (cancelled || signal?.aborted) return;
        const frame = meta[taskIdx++];
        if (!frame) continue;
        const frameKey = `${frame.sop_instance_uid}_${frame.frame_number}`;
        // Skip already loaded, failed, or currently being priority-fetched
        if (blobUrlsRef.current[frameKey] || failedFrameKeysRef.current.has(frameKey)) continue;
        // If priority fetch is in-flight for this frame, wait briefly and skip (it will complete)
        if (loadingRef.current.has(frameKey)) continue;
        loadingRef.current.add(frameKey);
        try {
          const cached = getCachedFrameImageUrl(frame.image_url);
          const blobUrl = cached ?? (frame.image_url ? await fetchFrameImage(frame.image_url, { signal }) : null);
          if (cancelled || signal?.aborted) { loadingRef.current.delete(frameKey); return; }
          if (!blobUrl) { loadingRef.current.delete(frameKey); continue; }
          blobUrlsRef.current[frameKey] = blobUrl;
          pendingLoadCountRef.current++;
          if (frameKey === currentImageIdRef.current) setCurrentFrameBlobVersion((v) => v + 1);
        } catch (e: any) {
          if (e?.name === 'AbortError') { loadingRef.current.delete(frameKey); return; }
          failedFrameKeysRef.current.add(frameKey);
        } finally {
          loadingRef.current.delete(frameKey);
        }
      }
    };

    // Launch CONCURRENT_FETCHES workers all consuming from the same taskIdx queue
    const workers = Array.from({ length: Math.min(CONCURRENT_FETCHES, meta.length) }, worker);
    Promise.all(workers).catch(() => { });

    return () => { cancelled = true; };
  }, [allFramesMetaLength, isVideoInstance, frameViewMode, instance.sop_instance_uid, lazyImageFetch]);

  // ── Smart/lazy fetch (report finding explainability): proving frames first, then a sliding window ──
  // Strategy (only active when `lazyImageFetch` is on, single-view DICOM):
  //   • idle  → fetch the proving frames (priorityFrameIds) + the current frame only.
  //   • playing → fetch a small window (LAZY_PLAY_PREFETCH_AHEAD) ahead of the current frame with
  //     limited concurrency (LAZY_PLAY_CONCURRENT). Because buffered playback only advances over
  //     already-loaded frames, this effect re-runs on each advance (currentImageId dep) and the
  //     window slides forward until the clip end — so we never blast every frame at once (which
  //     previously overwhelmed the backend and produced 500s / blank frames).
  const priorityFrameIdsKey = (priorityFrameIds ?? []).join(',');
  useEffect(() => {
    if (!lazyImageFetch || allFramesMetaLength === 0 || isVideoInstance || frameViewMode === 'grid') return;
    const meta = allFramesMetaRef.current;
    const signal = abortControllerRef.current?.signal;
    if (!meta.length || signal?.aborted) return;

    const keyOf = (f: any) => `${f.sop_instance_uid}_${f.frame_number}`;

    // Pick the frame to open on: the requested initialFrameId (first proving frame) when it
    // resolves, otherwise the clip's first frame. A functional update is essential here: on the
    // commit where frames first load, the initialFrameId jump effect (declared earlier) has already
    // scheduled setCurrentImageId(target) but currentImageIdRef is still stale (null) this commit,
    // so a plain `if (ref == null) set(meta[0])` would clobber that jump and open on frame 1.
    // `prev ?? …` respects the jump's value while still guaranteeing something is displayed.
    const initialFrame = initialFrameId
      ? meta.find((f: any) => String(f.frame_id) === initialFrameId)
      : undefined;
    const initialKey = keyOf(initialFrame ?? meta[0]);
    setCurrentImageId((prev) => prev ?? initialKey);

    // Resolve the current frame's index (fallback to 0).
    const curKey = currentImageIdRef.current;
    const curIdx = curKey ? meta.findIndex((f: any) => keyOf(f) === curKey) : 0;
    const startIdx = curIdx < 0 ? 0 : curIdx;

    // Build the ordered fetch queue.
    const idxByFrameId = new Map<string, number>();
    meta.forEach((f: any, i: number) => idxByFrameId.set(String(f.frame_id), i));

    const order: number[] = [];
    // 1) proving frames (in the order they were provided)
    (priorityFrameIds ?? []).forEach((fid) => {
      const i = idxByFrameId.get(String(fid));
      if (i !== undefined) order.push(i);
    });
    // 2) the currently displayed frame
    order.push(startIdx);
    // 3) on play: only a sliding window ahead of the current frame (the window slides as playback
    //    advances, so the whole clip still gets cached — just gradually, not all at once).
    if (isPlaying) {
      const ahead = Math.min(meta.length, startIdx + 1 + LAZY_PLAY_PREFETCH_AHEAD);
      for (let i = startIdx; i < ahead; i++) order.push(i);
    }

    // De-duplicate while preserving order.
    const seen = new Set<number>();
    const queue = order.filter((i) => (i >= 0 && i < meta.length && !seen.has(i) ? (seen.add(i), true) : false));
    if (queue.length === 0) return;

    let cancelled = false;
    let qptr = 0;
    const worker = async () => {
      while (qptr < queue.length) {
        if (cancelled || signal?.aborted) return;
        const frame = meta[queue[qptr++]];
        if (!frame) continue;
        const frameKey = keyOf(frame);
        if (blobUrlsRef.current[frameKey] || failedFrameKeysRef.current.has(frameKey) || loadingRef.current.has(frameKey)) continue;
        loadingRef.current.add(frameKey);
        try {
          const cached = getCachedFrameImageUrl(frame.image_url);
          const blobUrl = cached ?? (frame.image_url ? await fetchFrameImage(frame.image_url, { signal }) : null);
          if (cancelled || signal?.aborted) { loadingRef.current.delete(frameKey); return; }
          if (!blobUrl) { loadingRef.current.delete(frameKey); continue; }
          blobUrlsRef.current[frameKey] = blobUrl;
          if (frame.image_url) lazySessionRef.current.set(frameKey, frame.image_url);
          pendingLoadCountRef.current++;
          if (frameKey === currentImageIdRef.current) setCurrentFrameBlobVersion((v) => v + 1);
        } catch (e: any) {
          if (e?.name === 'AbortError') { loadingRef.current.delete(frameKey); return; }
          failedFrameKeysRef.current.add(frameKey);
        } finally {
          loadingRef.current.delete(frameKey);
        }
      }
    };
    const workers = Array.from({ length: Math.min(LAZY_PLAY_CONCURRENT, queue.length) }, worker);
    Promise.all(workers).catch(() => { });

    return () => { cancelled = true; };
    // currentImageId is intentionally a dep so scrubbing rebuilds the play queue from the new position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyImageFetch, allFramesMetaLength, isVideoInstance, frameViewMode, isPlaying, currentImageId, priorityFrameIdsKey, instance.sop_instance_uid, initialFrameId]);

  // ── Lazy mode: free this session's blobs when the viewer unmounts (bounded memory) ──
  useEffect(() => {
    if (!lazyImageFetch) return;
    const session = lazySessionRef.current;
    return () => {
      session.forEach((imageUrl, frameKey) => {
        const url = blobUrlsRef.current[frameKey];
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        removeCachedFrameImageUrl(imageUrl); // drop the (now-revoked) URL from the module cache
        delete blobUrlsRef.current[frameKey];
      });
      session.clear();
    };
  }, [lazyImageFetch]);

  // ── VIDEO grid mode: trigger backend warmup so thumbnails are pre-cached ───────────────────
  // The backend runs one sequential FFmpeg pass (no random seeks → fast) and fills the
  // thumbnail cache progressively. On-demand requests hit the cache, so every frame the
  // IO observer requests will return in ~0 ms once warmup has reached that frame.
  useEffect(() => {
    if (!isVideoInstance || frameViewMode !== 'grid' || !instance.sop_instance_uid) return;
    triggerVideoThumbnailWarmup(instance.sop_instance_uid);
  }, [isVideoInstance, frameViewMode, instance.sop_instance_uid]);

  // ── Grid mode: lazy-load thumbnails via IntersectionObserver (DICOM and VIDEO) ──────────────
  // Both video and DICOM use the backend thumbnail endpoint — it runs FFmpeg co-located
  // with storage (2–6 ms/frame) which is far faster than browser-side range-request seeking
  // (~100–300 ms/frame due to GCS/Azure internet latency).
  //
  // Memory is controlled by a component-level LRU: when more than MAX_GRID_BLOBS blobs are
  // loaded, the LEAST-RECENTLY-VIEWED frames are revoked first. Visible and recently-scrolled
  // frames are always the most-recently-used, so they are never evicted → no disappearing images.
  useEffect(() => {
    if (frameViewMode === 'single' || allFramesMetaLength === 0) return;
    const signal = abortControllerRef.current?.signal;
    if (signal?.aborted) return;

    const metaMap = new Map<string, any>();
    allFramesMetaRef.current.forEach((f: any) => {
      metaMap.set(`${f.sop_instance_uid}_${f.frame_number}`, f);
    });

    const maxConcurrent = isVideoInstance ? VIDEO_THUMB_CONCURRENT : DICOM_GRID_CONCURRENT;
    let cancelled = false;

    // ── LRU eviction: revoke the least-recently-viewed blob when cap is exceeded ──
    const touchLru = (frameKey: string) => {
      const idx = blobLruRef.current.indexOf(frameKey);
      if (idx >= 0) blobLruRef.current.splice(idx, 1);
      blobLruRef.current.push(frameKey); // move to most-recent end
    };

    const evictLru = () => {
      while (blobLruRef.current.length > MAX_GRID_BLOBS) {
        const evictKey = blobLruRef.current.shift()!; // oldest
        const url = blobUrlsRef.current[evictKey];
        if (url) {
          URL.revokeObjectURL(url);
          delete blobUrlsRef.current[evictKey];
        }
        const imageUrl = blobKeyToImageUrlRef.current.get(evictKey);
        if (imageUrl) {
          removeCachedFrameImageUrl(imageUrl); // prevent stale revoked URL being returned from module cache
          blobKeyToImageUrlRef.current.delete(evictKey);
        }
        failedFrameKeysRef.current.delete(evictKey); // allow re-fetch on next scroll-into-view
      }
    };

    // ── Concurrency-limited fetch queue ─────────────────────────────────────────
    const queue: string[] = [];
    let active = 0;

    const processNext = async (): Promise<void> => {
      if (cancelled || signal?.aborted || active >= maxConcurrent || queue.length === 0) return;
      const frameKey = queue.shift()!;
      if (blobUrlsRef.current[frameKey] || loadingRef.current.has(frameKey) || failedFrameKeysRef.current.has(frameKey)) {
        return processNext();
      }
      active++;
      loadingRef.current.add(frameKey);
      try {
        const frame = metaMap.get(frameKey);
        if (!frame?.image_url) return;
        const cached = getCachedFrameImageUrl(frame.image_url);
        const blobUrl = cached ?? await fetchFrameImage(frame.image_url, { signal });
        if (cancelled || signal?.aborted) return;
        if (blobUrl) {
          blobUrlsRef.current[frameKey] = blobUrl;
          blobKeyToImageUrlRef.current.set(frameKey, frame.image_url);
          blobLruRef.current.push(frameKey);
          evictLru();
          pendingLoadCountRef.current++;
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') failedFrameKeysRef.current.add(frameKey);
      } finally {
        if (!cancelled && !signal?.aborted) {
          active--;
          loadingRef.current.delete(frameKey);
          processNext();
        }
      }
    };

    const enqueue = (frameKey: string) => {
      if (cancelled || signal?.aborted) return;
      if (blobUrlsRef.current[frameKey] || loadingRef.current.has(frameKey) || failedFrameKeysRef.current.has(frameKey)) return;
      if (queue.includes(frameKey)) return;
      queue.push(frameKey);
      for (let i = active; i < maxConcurrent; i++) processNext();
    };

    // ── IntersectionObserver: enqueue visible cells, touch LRU for loaded cells ──
    const setupObserver = () => {
      const container = gridContainerRef.current;
      if (!container || cancelled) return;
      // Reuse the existing observer if one is already set up (e.g. after a page change).
      if (!gridObserverRef.current) {
        gridObserverRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              const fk = (entry.target as HTMLElement).dataset.frameKey;
              if (!fk) return;
              if (entry.isIntersecting) {
                // If already loaded, just refresh its LRU position (keeps it from being evicted)
                if (blobUrlsRef.current[fk]) touchLru(fk);
                else enqueue(fk);
              }
            });
          },
          {
            root: container,
            // Preload 600 px ahead so thumbnails appear before the user scrolls to them.
            rootMargin: '0px 0px 600px 0px',
            threshold: 0,
          }
        );
      }
      // Observe all current cells (new page cells are added here)
      container.querySelectorAll<HTMLElement>('[data-frame-key]').forEach(el => gridObserverRef.current!.observe(el));
    };

    const raf = requestAnimationFrame(setupObserver);

    return () => {
      cancelled = true;
      gridObserverRef.current?.disconnect();
      gridObserverRef.current = null;
      cancelAnimationFrame(raf);
    };
  }, [isVideoInstance, frameViewMode, allFramesMetaLength, instance.sop_instance_uid]);

  // When the grid page changes, new cells render but the IntersectionObserver doesn't know
  // about them automatically. Re-observe all current cells after the DOM updates.
  useEffect(() => {
    if (frameViewMode !== 'grid' || !gridObserverRef.current) return;
    const raf = requestAnimationFrame(() => {
      const container = gridContainerRef.current;
      if (!container || !gridObserverRef.current) return;
      container.querySelectorAll<HTMLElement>('[data-frame-key]').forEach(el =>
        gridObserverRef.current!.observe(el),
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [frameViewMode, gridPage]);

  // VIDEO: when video is ready, seek to the target frame or first frame.
  useEffect(() => {
    if (!isVideoInstance || !videoReady || !videoStreamData || !videoElementRef.current || allFramesMeta.length === 0) return;
    // Prefer frame_id lookup (unique), fall back to index, then first frame.
    const targetFrame = initialFrameId
      ? (allFramesMeta.find((f: any) => String(f.frame_id) === initialFrameId) ?? allFramesMeta[0])
      : (initialFrameIdx !== undefined && initialFrameIdx !== null)
        ? allFramesMeta[initialFrameIdx] ?? allFramesMeta[0]
        : allFramesMeta[0];
    const targetId = `${targetFrame.sop_instance_uid}_${targetFrame.frame_number}`;
    setCurrentImageId((prev) => (prev == null ? targetId : prev));
    videoElementRef.current.currentTime = (targetFrame.frame_number as number) / videoStreamData.fps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoInstance, videoReady, videoStreamData, allFramesMeta.length]);

  // VIDEO: clamp playback FPS to video's native FPS so canvas FPS selector stays compatible.
  useEffect(() => {
    if (!isVideoInstance || !videoStreamData?.fps) return;
    const videoFps = Math.max(1, Math.round(videoStreamData.fps));
    setPlayFps((p) => Math.min(p, videoFps));
  }, [isVideoInstance, videoStreamData?.fps]);

  const isPlayingRef = useRef(false);

  // VIDEO: sync currentImageId from video.currentTime.
  // Map: video.currentTime → actual video frame number → closest frame in allFramesMeta.
  // When filters are active allFramesMeta is a sparse subset of the full frame list, so we
  // cannot use the index directly — we must match on frame_number.
  useEffect(() => {
    if (!isVideoInstance || !videoReady || !videoStreamData || !videoElementRef.current || allFramesMeta.length === 0) return;
    const video = videoElementRef.current;
    const fps = videoStreamData.fps;
    const meta = allFramesMetaRef.current;
    const onTimeUpdate = () => {
      const actualFrameNumber = Math.round(video.currentTime * fps);
      // Find the meta entry whose frame_number is closest to actualFrameNumber
      let closestIdx = 0;
      let minDiff = Infinity;
      for (let i = 0; i < meta.length; i++) {
        const diff = Math.abs((meta[i].frame_number as number) - actualFrameNumber);
        if (diff < minDiff) { minDiff = diff; closestIdx = i; }
        if (diff === 0) break;
      }
      const frameId = `${meta[closestIdx].sop_instance_uid}_${meta[closestIdx].frame_number}`;
      setCurrentImageId(frameId);
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [isVideoInstance, videoReady, videoStreamData, allFramesMeta.length]);

  // VIDEO: when user scrubs (prev/next/slider) while paused, seek video to that frame's time.
  // Use frame_number (not meta index) so the correct video position is always used,
  // even when allFramesMeta is a filtered sparse subset.
  useEffect(() => {
    if (!isVideoInstance || !videoReady || !videoStreamData || !videoElementRef.current || !currentImageId || allFramesMeta.length === 0) return;
    if (isPlayingRef.current) return; // playback loop drives currentImageId; don't seek here.
    const meta = allFramesMetaRef.current;
    const frame = meta.find((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId);
    if (!frame) return;
    const video = videoElementRef.current;
    const fps = videoStreamData.fps;
    const targetTime = (frame.frame_number as number) / fps;
    if (Math.abs(video.currentTime - targetTime) > 0.05) video.currentTime = targetTime;
  }, [isVideoInstance, videoReady, videoStreamData, currentImageId, allFramesMeta.length]);

  // ── 3. Build DrawingCanvas image list ───────────────────────────────────────
  // When we have 200+ frames, only keep a window (e.g. 101 frames); clean the rest so we don't hold 2041 in memory (avoids 4GB crash).
  const imagesForDrawingCanvas: ImageInput[] = useMemo(() => {
    const len = allFramesMeta.length;
    const currentIdx = currentImageId != null
      ? allFramesMeta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId)
      : 0;
    const safeIdx = currentIdx >= 0 ? currentIdx : 0;
    const shapesLo = Math.max(0, safeIdx - SHAPES_WINDOW);
    const shapesHi = Math.min(len - 1, safeIdx + SHAPES_WINDOW);

    const virtualize = len > FRAME_LIST_WINDOW_THRESHOLD;
    // Always center on the current frame so the window ALWAYS contains it.
    // A separate windowCenterIndex state caused a 1-render lag: the window hadn't
    // shifted yet when DrawingCanvas rendered, so initialCurrentImageId pointed to
    // a frame outside the window. DrawingCanvas then called onImageChange with the
    // first frame of the old window, overriding the slider/playback position and
    // creating the "Maximum update depth exceeded" loop.
    const center = virtualize ? safeIdx : 0;
    const windowStart = virtualize ? Math.max(0, center - FRAME_LIST_WINDOW_HALF) : 0;
    const windowEnd = virtualize ? Math.min(len, center + FRAME_LIST_WINDOW_HALF + 1) : len;
    const framesToMap = allFramesMeta.slice(windowStart, windowEnd);

    return framesToMap.map((frame: any, i: number) => {
      const index = windowStart + i;
      const frameKey = `${frame.sop_instance_uid}_${frame.frame_number}`;
      // Read from ref (not state) so only currentFrameBlobVersion triggers a recompute,
      // not every blob that arrives in the background.
      const src = isVideoInstance ? PLACEHOLDER_SRC : (blobUrlsRef.current[frameKey] || PLACEHOLDER_SRC);

      const inWindow = index >= shapesLo && index <= shapesHi;
      const shapes: Shape[] = [];

      let pixelSpacing: { x: number; y: number } | undefined;
      if (Array.isArray(frame.pixel_spacing) && frame.pixel_spacing.length >= 2) {
        pixelSpacing = { x: frame.pixel_spacing[0], y: frame.pixel_spacing[1] };
      }

      if (inWindow && Array.isArray(frame.segmentations)) {
        const segmentationsForLoad = dedupeSegmentationsForLoad(
          frame.segmentations,
          categoryIdsWithMaxTwo,
          measLabelToCategoryId,
          categoryIdToMeasLabel,
          categories
        );
        // Deduplicate ELLIPSE/POLYLINE: when structure + measurement share same biometry slot & points, skip the structure
        const measKeys = new Set<string>();
        for (const seg of segmentationsForLoad as any[]) {
          const geo = (seg.geometry_type ?? '').toUpperCase();
          if (
            (geo === 'ELLIPSE' || geo === 'POLYLINE') &&
            (seg.segment_type === 'measurement' || seg.label?.startsWith('MEAS__'))
          ) {
            const key = getSegmentationStructureDedupeKey(
              seg,
              measLabelToCategoryId,
              categoryIdToMeasLabel
            );
            if (key) measKeys.add(key);
          }
        }
        segmentationsForLoad.filter((seg: any) => {
          const geo = (seg.geometry_type ?? '').toUpperCase();
          if (
            (geo === 'ELLIPSE' || geo === 'POLYLINE') &&
            seg.segment_type !== 'measurement' &&
            !seg.label?.startsWith('MEAS__')
          ) {
            const key = getSegmentationStructureDedupeKey(
              seg,
              measLabelToCategoryId,
              categoryIdToMeasLabel
            );
            if (key && measKeys.has(key)) {
              return false;
            }
          }
          return true;
        }).forEach((seg: any) => {
          const rawShape = convertSegmentationToRawShape(seg, frameKey);
          if (rawShape) {
            try {
              const shape = rawShapeToShape(rawShape);
              const resolved = resolveSegmentationLabelsForLoad(
                seg,
                categoryIdToMeasLabel,
                measLabelToCategoryId,
                undefined,
                measGeometryContext
              );
              shapes.push(
                enrichShapeWithMeasurementOverlay(
                  {
                    ...shape,
                    label: resolved.displayLabel,
                    color:
                      seg.category_id != null
                        ? getColorForCategoryId(seg.category_id)
                        : getColorForCode(seg.label ?? undefined),
                    dbId: seg.id,
                    backendLabel: resolved.backendLabel,
                    backendType: getSegGeometryType(seg),
                    categoryId:
                      seg.category_id ??
                      resolveSegmentationCategoryId(seg, measLabelToCategoryId),
                    segmentType: resolved.segmentType,
                    isManual: seg.is_manual === true,
                  },
                  seg,
                  frame.measurements || [],
                  pixelSpacing,
                  categoryIdToMeasLabel,
                  measLabelToCategoryId,
                  measGeometryContext,
                  frame.pixel_spacing_unit || undefined,
                  categoryIdToMeasNames
                ) as Shape
              );
            } catch (_) { }
          }
        });
      }

      linkAndSyncDerivedBpdShapes(
        shapes as any[],
        categories,
        frame.measurements || [],
        pixelSpacing,
        frame.pixel_spacing_unit || undefined,
        measLabelToCategoryId
      );

      return {
        id: frameKey,
        name: `Frame ${frame.frame_number}`,
        src,
        initialShapes: shapes,
        pixelSpacing,
        pixel_spacing_unit: frame.pixel_spacing_unit || undefined,
      };
    });
    // currentFrameBlobVersion replaces blobUrls as the dep — only increments when the CURRENT
    // frame's blob loads, not for every background blob, eliminating the render-loop.
  }, [allFramesMeta, currentImageId, currentFrameBlobVersion, getColorForCategoryId, getColorForCode, isVideoInstance, categoryIdsWithMaxTwo, measLabelToCategoryId, categoryIdToMeasLabel, categories, measGeometryContext]);

  // ── Stable initial image id ─────────────────────────────────────────────────
  const prevImagesKeyRef = useRef<string>('');
  const stableInitialImageIdRef = useRef<string | undefined>(undefined);
  const imagesKey = imagesForDrawingCanvas.map((img) => img.id).join('\0');
  if (imagesKey !== prevImagesKeyRef.current) {
    prevImagesKeyRef.current = imagesKey;
    // Only keep the current position when the current frame still exists in the
    // new list. When filters remove the current frame (or currentImageId is reset
    // to undefined) always go to frame 0 of the new list.
    const currentStillPresent =
      !!currentImageId && imagesForDrawingCanvas.some((img) => img.id === currentImageId);
    stableInitialImageIdRef.current = currentStillPresent
      ? currentImageId
      : imagesForDrawingCanvas[0]?.id;
  }
  // If currentImageId is defined but not in the current list (stale after filter
  // change before the reset effect fires), ignore it and use the stable ref.
  const currentInList =
    !!currentImageId && imagesForDrawingCanvas.some((img) => img.id === currentImageId);
  const initialCurrentImageId = currentInList ? currentImageId : stableInitialImageIdRef.current;

  // Set while a programmatic jump is in-flight (goToFrameIndex call).
  // Suppresses the immediate DrawingCanvas onImageChange echo that fires when the
  // isJumpingRef: blocks DC callbacks during an in-flight programmatic jump (same-render echo).
  const isJumpingRef = useRef(false);
  // jumpTargetRef: records the intended target of the latest goToFrameIndex call.
  // Stale useEffect callbacks from intermediate renders (e.g. rapid slider drag) fire AFTER
  // the rAF that clears isJumpingRef. Without this guard they would re-set currentImageId to
  // a wrong intermediate frame and permanently block mouse-wheel scroll.
  // React runs effects in commit order, so all intermediate effects fire before the final
  // render's effect (which confirms the target and clears this ref).
  const jumpTargetRef = useRef<string | null>(null);
  // Fallback: for video instances, DC's onImageChange confirmation is delayed by async blob
  // loading. This timeout ensures jumpTargetRef is always cleared within 100 ms so scroll
  // is never permanently blocked.  For DICOM, DC confirms in ~32 ms so the timeout is a no-op.
  const jumpClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleImageChange = useCallback((imageId: string) => {
    // ── goToFrameIndex path ──────────────────────────────────────────────────────
    // jumpTargetRef is set whenever goToFrameIndex fires (slider, prev/next, jump input).
    // Check it FIRST — even before isJumpingRef — so the DC confirmation echo always
    // reaches this branch regardless of whether the rAF that clears isJumpingRef has
    // fired yet. If we checked isJumpingRef first, the confirmation would be swallowed
    // while isJumpingRef is still true, leaving jumpTargetRef set permanently and
    // blocking every subsequent mouse-wheel scroll event.
    if (jumpTargetRef.current !== null) {
      if (imageId === jumpTargetRef.current) {
        // DC confirmed the intended frame — unlock scroll.
        jumpTargetRef.current = null;
      }
      // Stale intermediate render echo: ignore it; parent already has the right state.
      return;
    }
    // ── VIDEO: ignore late blob-load callbacks that would revert user scroll ─────
    // When the user scrolls to frame 251, the blob for frame 250 may still be loading.
    // When it completes, DC shows it and fires onImageChange("sop_250"), which would
    // set currentImageId back to 250 and undo the scroll. Don't apply when the incoming
    // frame is behind the current (by index) so we never revert a forward navigation.
    if (isVideoInstance) {
      const meta = allFramesMetaRef.current;
      const currentIdx = currentImageIdRef.current != null
        ? meta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageIdRef.current)
        : -1;
      const incomingIdx = meta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === imageId);
      if (currentIdx >= 0 && incomingIdx >= 0 && incomingIdx < currentIdx) return;
    }
    // ── Direct DC navigation path (mouse wheel, internal arrows) ─────────────────
    // Block in-flight echo from the same render cycle.
    if (isJumpingRef.current) return;
    // During DICOM playback the rAF loop owns currentImageId.
    if (isPlayingRef.current && !isVideoInstance) return;
    // Confirmation echo after window-shift correction — same frame, nothing to do.
    if (imageId === currentImageIdRef.current) return;
    // Real navigation: raise the flag so the window-shift echo on the next re-render
    // is suppressed for exactly one animation frame.
    isJumpingRef.current = true;
    requestAnimationFrame(() => { isJumpingRef.current = false; });
    setCurrentImageId(imageId);
  }, [isVideoInstance]);

  // Jump to any frame by zero-based index.
  const goToFrameIndex = useCallback((zeroBasedIndex: number) => {
    const meta = allFramesMetaRef.current;
    if (zeroBasedIndex < 0 || zeroBasedIndex >= meta.length) return;
    const f = meta[zeroBasedIndex];
    const targetId = `${f.sop_instance_uid}_${f.frame_number}`;
    // Record the target so stale effects from intermediate slider renders are rejected.
    jumpTargetRef.current = targetId;
    // Fallback: clear the target after 100 ms. For DICOM, DC confirms in ~32 ms and this
    // fires as a no-op. For video, DC confirmation is gated on async blob loading which can
    // take longer; the timeout guarantees scroll is never permanently blocked.
    // Each new goToFrameIndex call resets the timer so only the LAST call's timeout fires.
    if (jumpClearTimeoutRef.current) clearTimeout(jumpClearTimeoutRef.current);
    jumpClearTimeoutRef.current = setTimeout(() => {
      jumpTargetRef.current = null;
      jumpClearTimeoutRef.current = null;
    }, 100);
    // Block the immediate window-shift echo (fires in the same render cycle).
    isJumpingRef.current = true;
    requestAnimationFrame(() => { isJumpingRef.current = false; });
    setCurrentImageId(targetId);
  }, []);

  // Uncontrolled input ref for the frame-jump box. Updated externally only when not focused.
  const frameJumpInputRef = useRef<HTMLInputElement | null>(null);

  // ── Playback ────────────────────────────────────────────────────────────────
  // Keep the frame-jump input display in sync with playback / prev-next without
  // making it a controlled input (which would fire onChange every frame).
  // We read sliderIdx during the render phase and write to the DOM imperatively.
  if (frameJumpInputRef.current && document.activeElement !== frameJumpInputRef.current) {
    frameJumpInputRef.current.value = String(
      currentImageId
        ? Math.max(1, allFramesMeta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId) + 1) || 1
        : 1
    );
  }
  /** requestAnimationFrame handle for DICOM playback. */
  const rafRef = useRef<number | null>(null);
  /** Timestamp of the last frame advance for drift-corrected rAF timing. */
  const playbackLastFrameTimeRef = useRef<number>(0);
  /** Direct index into allFramesMeta for the rAF loop — O(1) advance, no findIndex on every frame. */
  const rafPlaybackIdxRef = useRef<number>(-1);
  /** Container div wrapping the image area. */
  const playbackContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  // DICOM: requestAnimationFrame loop.
  // Uses rafPlaybackIdxRef (O(1) advance) and always drives React state so DrawingCanvas
  // stays in sync with segmentations visible at all frame rates.
  useEffect(() => {
    if (isVideoInstance) return;
    if (!isPlaying) {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      return;
    }

    const meta = allFramesMetaRef.current;
    if (meta.length === 0) return;

    const startId = currentImageIdRef.current;
    let startIdx = startId != null
      ? meta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === startId)
      : -1;
    if (startIdx < 0 || startIdx >= meta.length - 1) startIdx = 0;
    rafPlaybackIdxRef.current = startIdx;

    const frameDuration = 1000 / playFps;
    playbackLastFrameTimeRef.current = performance.now() - frameDuration;

    // Buffered playback (lazy explainability viewers): never advance onto a frame whose image
    // hasn't been fetched yet — hold on the current frame (retrying transient failures) until the
    // next one is ready, so frames play success-after-success with no blank placeholders.
    const buffered = lazyImageFetch;
    const keyAt = (idx: number, m: any[]) => `${m[idx].sop_instance_uid}_${m[idx].frame_number}`;

    const loop = (timestamp: number) => {
      if (!isPlayingRef.current) {
        rafRef.current = null;
        const idx = rafPlaybackIdxRef.current;
        const m = allFramesMetaRef.current;
        if (idx >= 0 && idx < m.length) setCurrentImageId(keyAt(idx, m));
        return;
      }

      const m = allFramesMetaRef.current;
      const nextIdx = rafPlaybackIdxRef.current + 1;

      if (nextIdx >= m.length) {
        rafRef.current = null;
        const lastFrame = m[m.length - 1];
        setCurrentImageId(`${lastFrame.sop_instance_uid}_${lastFrame.frame_number}`);
        setIsPlaying(false);
        isPlayingRef.current = false;
        return;
      }

      if (buffered) {
        const nextKey = keyAt(nextIdx, m);
        if (!blobUrlsRef.current[nextKey]) {
          if (failedFrameKeysRef.current.has(nextKey)) {
            const tries = lazyRetryRef.current.get(nextKey) ?? 0;
            if (tries < LAZY_PLAY_MAX_RETRIES) {
              // Transient failure (e.g. 500) — clear the flag, re-fetch, and keep holding.
              lazyRetryRef.current.set(nextKey, tries + 1);
              failedFrameKeysRef.current.delete(nextKey);
              loadCurrentFrameNow(nextKey);
            } else {
              // Permanently unfetchable — skip past it without ever showing a blank frame.
              rafPlaybackIdxRef.current = nextIdx;
              rafRef.current = requestAnimationFrame(loop);
              return;
            }
          } else {
            loadCurrentFrameNow(nextKey); // ensure a fetch is in-flight, then wait
          }
          // Hold on the current frame and restart the frame clock so pacing resumes cleanly.
          playbackLastFrameTimeRef.current = timestamp;
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
      }

      const elapsed = timestamp - playbackLastFrameTimeRef.current;
      if (elapsed >= frameDuration) {
        playbackLastFrameTimeRef.current = timestamp - (elapsed % frameDuration);
        rafPlaybackIdxRef.current = nextIdx;
        setCurrentImageId(keyAt(nextIdx, m));
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [isPlaying, playFps, isVideoInstance, lazyImageFetch, loadCurrentFrameNow]);

  // VIDEO: play/pause.
  // When filters are active (allFramesMeta is a sparse subset), native video.play() would
  // show frames that don't match the filter. Use a stepped interval that seeks the video
  // to each filtered frame's timestamp instead — same behaviour as image mode playback.
  // When no filters, use native play() at an adjusted playbackRate (original behaviour).
  useEffect(() => {
    if (!isVideoInstance || !videoElementRef.current || !videoStreamData || allFramesMetaLength === 0) {
      if (videoElementRef.current) videoElementRef.current.pause();
      return;
    }
    const video = videoElementRef.current;
    const fps = videoStreamData.fps;

    if (!isPlaying) {
      video.pause();
      return;
    }

    const meta = allFramesMetaRef.current;
    const startId = currentImageIdRef.current;
    let startIdx = meta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === startId);
    if (startIdx < 0 || startIdx >= meta.length - 1) startIdx = 0;

    // Detect whether the metadata is a filtered subset of the full video.
    const totalVideoFrames = videoStreamData.total_frames || 0;
    const isFiltered = totalVideoFrames > 0 && meta.length < totalVideoFrames;

    if (isFiltered) {
      // Stepped playback: seek video to each filtered frame's timestamp at playFps rate.
      // This ensures only filtered frames are shown, matching image-mode behaviour.
      let currentStepIdx = startIdx;
      video.pause();

      const stepInterval = setInterval(() => {
        const m = allFramesMetaRef.current;
        currentStepIdx++;
        if (currentStepIdx >= m.length) {
          clearInterval(stepInterval);
          setIsPlaying(false);
          isPlayingRef.current = false;
          return;
        }
        const frame = m[currentStepIdx];
        video.currentTime = (frame.frame_number as number) / fps;
        setCurrentImageId(`${frame.sop_instance_uid}_${frame.frame_number}`);
      }, 1000 / playFps);

      return () => clearInterval(stepInterval);
    }

    // No active filters — native playback at adjusted rate.
    video.currentTime = (meta[startIdx].frame_number as number) / fps;
    video.playbackRate = Math.max(0.1, Math.min(2, playFps / fps));
    video.play().catch(() => { });
    return () => { video.pause(); };
  }, [isPlaying, playFps, isVideoInstance, videoStreamData, allFramesMetaLength]);

  const handleSave = async (
    shapes: Shape[],
    imageId: string,
    _imageName: string,
    deletedDbIds: (number | string)[] = []
  ) => {
    setIsPlaying(false);
    const lastUnderscore = imageId.lastIndexOf('_');
    if (lastUnderscore === -1) return;
    const sop_instance_uid = imageId.substring(0, lastUnderscore);
    const frame_number = parseInt(imageId.substring(lastUnderscore + 1), 10);
    if (isNaN(frame_number)) return;

    const currentFrame = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === imageId
    );
    if (!currentFrame?.frame_id) return;

    const currentImage = imagesForDrawingCanvas.find((img) => img.id === imageId);
    const changedShapes = shapes.filter((s: any) => s.isNew || s.isDirty);
    if (changedShapes.length === 0 && deletedDbIds.length === 0) {
      messageI18n.info('viewer.noChangesToSave');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        sop_instance_uid,
        frame_number,
        frame_id: currentFrame.frame_id,
        segmentations: convertShapesToSegmentations(
          changedShapes,
          categoryIdToMeasLabel,
          labelToMeasName,
          measLabelToCategoryId,
          categories,
          measGeometryContext
        ),
        delete_segmentation_ids: collectDuplicateCategoryDeleteIds(
          changedShapes,
          currentFrame?.segmentations,
          deletedDbIds,
          categoryIdsWithMaxTwo,
          categoryIdToMeasLabel,
          measLabelToCategoryId,
          labelToMeasName,
          categories
        ),
      };
      const result = await saveSegmentations(payload as any);
      if (result?.success) {
        const savedIds = result.saved_ids ?? [];
        const updatedIds = result.updated_ids ?? [];
        if (changedShapes.length > 0 && (savedIds.length > 0 || updatedIds.length > 0)) {
          drawingCanvasRef.current?.assignDbIdsAfterSave(changedShapes, savedIds, updatedIds);
        }
        drawingCanvasRef.current?.clearDirtyStates();
        drawingCanvasRef.current?.clearDeletedDbIds?.();
        messageI18n.success('viewer.changesSaved');
        await refetchMeta();
        setSyncShapesVersion((v) => v + 1);
        // Re-run validation so the checklist reflects the newly drawn structure immediately.
        // Use the ref so we always read the up-to-date pendingViewDefId from this async callback.
        const activePendingView = pendingViewDefIdRef.current;
        if (activePendingView && currentFrame?.frame_id) {
          try {
            const validationResult = await validateFrameForView(currentFrame.frame_id, activePendingView);
            setValidation(validationResult);
          } catch { /* ignore */ }
        }
      } else {
        message.error(result?.error || tToast('viewer.saveFailed'));
      }
    } catch {
      messageI18n.error('viewer.saveFailed');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Validate + preview a view before assigning ──────────────────────────────
  const handleSelectPendingView = useCallback(async (viewDefId: number | null) => {
    setPendingViewDefId(viewDefId);
    setValidation(null);
    if (!viewDefId) return;
    const currentFrame = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
    );
    if (!currentFrame?.frame_id) return;
    setIsValidating(true);
    try {
      const result = await validateFrameForView(currentFrame.frame_id, viewDefId);
      setValidation(result);
    } finally {
      setIsValidating(false);
    }
  }, [allFramesMeta, currentImageId, validateFrameForView]);

  // ── Assign-mode: auto-select view + validate on every frame change ───────────
  // Runs after handleSelectPendingView is defined so we can call it here.
  // Triggers whenever: the resolved view_def_id becomes available OR the current frame changes.
  useEffect(() => {
    const vid = resolvedInitialViewDefIdRef.current;
    if (!vid) return;           // not in assign mode
    if (!currentImageId) return; // frames not loaded yet
    // Always call handleSelectPendingView so the validation checklist is freshly computed
    // for the currently displayed frame — critical for showing required structures.
    handleSelectPendingView(vid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedInitialViewDefId, currentImageId]);

  // ── Assign (add/remove/clear) user view on current frame ────────────────────
  const handleAssignView = useCallback(async (viewDefId: number | null, remove = false) => {
    const currentFrame = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
    );
    if (!currentFrame?.frame_id) return;
    setIsAssigningView(true);
    try {
      const result = await assignUserView(currentFrame.frame_id, viewDefId, remove);
      if (result?.success) {
        if (viewDefId === null) messageI18n.success('viewer.allViewAssignmentsCleared');
        else if (remove) messageI18n.success('viewer.viewRemoved');
        else messageI18n.success('viewer.viewAssigned');
        setPendingViewDefId(null);
        setValidation(null);
        await refetchMeta();
      } else {
        message.error(result?.error || tToast('viewer.assignViewFailed'));
      }
    } catch {
      messageI18n.error('viewer.assignViewFailed');
    } finally {
      setIsAssigningView(false);
    }
  }, [allFramesMeta, currentImageId, assignUserView, refetchMeta]);

  // ── Confirm view: add to user_view_def_ids AND save frame to study report ────
  const handleConfirmAndSave = useCallback(async (viewDefId: number) => {
    const currentFrame = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
    );
    if (!currentFrame?.frame_id) return;
    setIsSavingToReport(true);
    try {
      // 1. Add to user_view_def_ids on frame (remove=false → adds if not already there)
      const assignResult = await assignUserView(currentFrame.frame_id, viewDefId, false);
      if (!assignResult?.success) {
        message.error(assignResult?.error || tToast('viewer.assignViewFailed'));
        return;
      }
      // 2. Add this frame to the study report for this view
      await apiClient.put('/study_reports', {
        study_instance_uid: studyUID,
        frame_id: currentFrame.frame_id,
        serie_instance_uid: currentFrame.serie_instance_uid,
        view_def_id: viewDefId,
      });
      messageI18n.success('viewer.viewConfirmedAdded');
      setPendingViewDefId(null);
      setValidation(null);
      lastAutoValidatedKey.current = null;
      await refetchMeta();
      // If we arrived here from the Views tab "Assign Image" button, navigate back to Views.
      if (initialPendingViewCode) {
        onViewConfirmedAndSaved?.();
      }
    } catch {
      messageI18n.error('viewer.confirmViewFailed');
    } finally {
      setIsSavingToReport(false);
    }
  }, [allFramesMeta, currentImageId, assignUserView, studyUID, refetchMeta, initialPendingViewCode, onViewConfirmedAndSaved]);

  // ── Clear pending view review when navigating to a different frame ───────────
  // This must run BEFORE the auto-validate effect so the panel resets immediately.
  // Exception: when arriving from the Views tab "Assign" button, we keep the pre-selected view
  // so the assign-mode effect above can re-validate it for the new frame.
  const prevImageIdForClear = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevImageIdForClear.current === currentImageId) return;
    prevImageIdForClear.current = currentImageId;
    // In assign mode: don't clear — the assign-mode effect will re-validate the same view.
    if (initialPendingViewCode && resolvedInitialViewDefIdRef.current) return;
    // Reset any view-review state left over from the previous frame
    setPendingViewDefId(null);
    setValidation(null);
    lastAutoValidatedKey.current = null;
  }, [currentImageId, initialPendingViewCode]);

  // ── Auto-validate whenever the active frame has a user-assigned view ─────────
  // Runs on frame change or after metadata refetch so the validation panel is up to date.
  useEffect(() => {
    if (pendingViewDefId) return; // user is actively reviewing a view — don't override with auto
    const frameMeta = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
    );
    if (!frameMeta?.frame_id) return;
    // Only validate when the user has explicitly assigned a view via the dropdown.
    // AI-only predictions don't trigger the conflict panel automatically.
    const viewId: number | null = ((frameMeta as any).user_view_def_ids?.[0]) ?? null;
    if (!viewId) {
      // No user-assigned view — clear any stale validation
      setValidation(null);
      lastAutoValidatedKey.current = null;
      return;
    }
    const key = `${frameMeta.frame_id}::${viewId}`;
    if (lastAutoValidatedKey.current === key) return; // already fetched for this frame+view
    lastAutoValidatedKey.current = key;
    setIsValidating(true);
    validateFrameForView(frameMeta.frame_id, viewId).then((result) => {
      setValidation(result);
    }).finally(() => {
      setIsValidating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageId, allFramesMeta]);

  // ── Conflict resolution: delete one or more segs, then re-validate ───────────
  const handleResolveConflict = useCallback(async (deleteSegIds: string | string[]) => {
    const currentFrame = allFramesMeta.find(
      (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
    );
    if (!currentFrame?.frame_id || !currentFrame?.sop_instance_uid) return;
    const ids = (Array.isArray(deleteSegIds) ? deleteSegIds : [deleteSegIds])
      .map((id) => (id != null ? String(id) : ''))
      .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
    if (ids.length === 0) return;
    try {
      const payload = {
        sop_instance_uid: currentFrame.sop_instance_uid,
        frame_number: Number(currentFrame.frame_number),
        frame_id: currentFrame.frame_id,
        segmentations: [],
        delete_segmentation_ids: ids,
      };
      await saveSegmentations(payload as any);
      lastAutoValidatedKey.current = null; // force re-validation after delete
      await refetchMeta();
      setSyncShapesVersion((v) => v + 1);
      // Re-validate after resolution using pending or first user-confirmed view
      const userViewIds: number[] = (currentFrame as any).user_view_def_ids ?? [];
      const vid = pendingViewDefId ?? userViewIds[0] ?? null;
      if (vid) {
        const result = await validateFrameForView(currentFrame.frame_id, vid);
        setValidation(result);
      }
    } catch {
      messageI18n.error('viewer.resolveConflictFailed');
    }
  }, [allFramesMeta, currentImageId, saveSegmentations, refetchMeta, pendingViewDefId, validateFrameForView]);

  // ── Per-frame derived state (hooks — must be before any early returns) ───────
  // Current frame metadata for the view assignment panel
  const currentFrameMeta = useMemo(() => {
    if (!currentImageId) return allFramesMeta[0] ?? null;
    return allFramesMeta.find((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId) ?? null;
  }, [currentImageId, allFramesMeta]);

  /** Per-slot user-vs-AI conflicts (BPD line ≠ HC ellipse on same category). */
  const structureConflicts = useMemo(() => {
    const segs = (currentFrameMeta as any)?.segmentations;
    if (!Array.isArray(segs) || segs.length === 0) return [];
    return groupStructureConflictsBySlot(
      segs,
      categoryIdToMeasLabel,
      measLabelToCategoryId
    );
  }, [currentFrameMeta, categoryIdToMeasLabel, measLabelToCategoryId]);

  const currentViewAgreement: ViewAgreement = (currentFrameMeta as any)?.view_agreement ?? 'none';
  /** All user-confirmed view IDs for the current frame (multi-view support). */
  const currentUserViewDefIds: number[] = useMemo(
    () => (currentFrameMeta as any)?.user_view_def_ids ?? [],
    [currentFrameMeta]
  );
  const currentMlViewDefId: number | null = (currentFrameMeta as any)?.view_def_id ?? null;
  /** All AI-predicted view IDs for the current frame. */
  const currentFrameMlViewDefIds: number[] = useMemo(
    () => (currentFrameMeta as any)?.frame_ml_view_def_ids ?? [],
    [currentFrameMeta]
  );

  const collaborationViewerTarget = useMemo(() => {
    const meta = currentFrameMeta as { frame_id?: string; frame_number?: number } | null;
    if (!meta?.frame_id) return null;
    const idx = currentImageId
      ? allFramesMeta.findIndex(
          (f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId
        )
      : -1;
    const displayNum =
      idx >= 0 ? idx + 1 : meta.frame_number != null ? meta.frame_number + 1 : 1;
    return buildGeneralViewerFrameTarget(String(meta.frame_id), displayNum);
  }, [currentFrameMeta, currentImageId, allFramesMeta]);

  useCollaborationViewerLocation(collaborationViewerTarget);

  useEffect(() => {
    if (!onLiveExportHandlersChange) return;
    if (allFramesMeta.length === 0 && !isMetaLoading) {
      onLiveExportHandlersChange(null);
      return;
    }
    const handlers: LiveViewerExportHandlers = {
      exportCurrentDicom: () => {
        if ((instance.format_file ?? 'DICOM') !== 'DICOM') {
          message.warning(t('export.notDicom'));
          return;
        }
        void runWithIndeterminateExportNotification(t('export.currentDicom'), () =>
          downloadDicomInstance(studyUID, instance)
        ).catch(() => {
          message.error(t('export.dicomFailed'));
        });
      },
      exportFrameJpeg: async () => {
        const meta = currentFrameMeta as { image_url?: string; frame_number?: number } | null;
        const imageUrl = meta?.image_url;
        if (!imageUrl) {
          message.warning(t('export.noImageUrl'));
          return;
        }
        try {
          await runWithIndeterminateExportNotification(t('export.currentFrameJpeg'), () =>
            downloadFrameJpegFromUrl(
              imageUrl,
              `frame_${instance.sop_instance_uid}_${meta?.frame_number ?? 1}.jpg`
            )
          );
        } catch {
          message.error(t('export.imageFailed'));
        }
      },
      exportViewPng: async () => {
        if (isVideoInstance) {
          message.warning(t('export.videoPngHint'));
          return;
        }
        try {
          await runWithIndeterminateExportNotification(t('export.currentViewPng'), async () => {
            const blob = await drawingCanvasRef.current?.exportViewToPngBlob();
            if (!blob) throw new Error('export.pngFailed');
            const fn = (currentFrameMeta as { frame_number?: number } | null)?.frame_number ?? 1;
            downloadBlobFile(blob, `view_${instance.sop_instance_uid}_${fn}.png`);
          });
        } catch {
          message.error(t('export.pngFailed'));
        }
      },
      canExportViewPng: !isVideoInstance,
      viewPngTitle: isVideoInstance ? t('export.videoPngHint') : undefined,
      instanceIsDicom: (instance.format_file ?? 'DICOM') === 'DICOM',
    };
    onLiveExportHandlersChange(handlers);
    return () => onLiveExportHandlersChange(null);
  }, [
    onLiveExportHandlersChange,
    studyUID,
    instance,
    currentFrameMeta,
    isVideoInstance,
    isMetaLoading,
    allFramesMeta.length,
    t,
  ]);

  // Map view_def_id -> display_name using filterOptions views list (views now include id)
  const viewDefDisplayName = useCallback((id: number | null): string => {
    if (!id || !filterOptions?.views?.length) return String(id ?? '');
    const found = filterOptions.views.find((v: any) => v.id === id);
    if (!found) return String(id);
    return t(`viewTag.${found.view_code}`, { defaultValue: found.display_name ?? String(id) });
  }, [filterOptions]);

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (allFramesMeta.length === 0 && !isMetaLoading) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm bg-white dark:bg-gray-950">
        {t('instanceDetail.noFramesFound')}
      </div>
    );
  }

  const loadedCount = isVideoInstance ? (videoReady ? 1 : 0) : loadedBlobCount;
  const totalCount = allFramesMeta.length;

  // Display index: for the frame counter label, track the blob-ready frame (video) or current frame (DICOM).
  const currentIdx = initialCurrentImageId
    ? (allFramesMeta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === initialCurrentImageId) + 1) || 1
    : 1;

  // Slider index: must follow currentImageId IMMEDIATELY (not the async blob delivery).
  // For video, initialCurrentImageId lags behind currentImageId by the async seek duration,
  // causing the slider to snap back visually. Reading from currentImageId directly fixes it.
  const sliderIdx = (() => {
    if (!currentImageId) return currentIdx;
    const raw = allFramesMeta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === currentImageId);
    return raw >= 0 ? raw + 1 : currentIdx;
  })();

  const isVirtualized = totalCount > FRAME_LIST_WINDOW_THRESHOLD;
  const showLoadingCount = !isVideoInstance && loadedCount < totalCount;

  // Agreement badge config
  const agreementBadge: Record<ViewAgreement, { label: string; color: string; icon: JSX.Element | null }> = {
    agree: { label: t('instanceDetail.agreement.agreed'), color: 'text-green-600  bg-green-50  border-green-200', icon: <CheckCircle2 size={11} /> },
    conflict: { label: t('instanceDetail.agreement.conflict'), color: 'text-red-600    bg-red-50    border-red-200', icon: <AlertTriangle size={11} /> },
    user_only: { label: t('instanceDetail.agreement.yourView'), color: 'text-blue-600   bg-blue-50   border-blue-200', icon: <Eye size={11} /> },
    ml_only: { label: t('instanceDetail.agreement.aiView'), color: 'text-purple-600 bg-purple-50 border-purple-200', icon: <Bot size={11} /> },
    none: { label: t('instanceDetail.agreement.noView'), color: 'text-gray-500   bg-gray-50   border-gray-200', icon: null },
  };
  const badge = agreementBadge[currentViewAgreement];

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-950">

      {/* ── Assign-mode banner (shown when arriving from Views tab "Assign Image") ── */}
      {initialPendingViewCode && (
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm">🎯</span>
            <span className="text-xs font-semibold text-amber-800 dark:text-amber-300 truncate">
              {t('instanceDetail.assignBanner.assignImageTo')}{' '}
              <span className="font-bold">
                {viewDefDisplayName(resolvedInitialViewDefId) || initialPendingViewCode.replace(/^VIEW__/, '').replace(/_/g, ' ')}
              </span>
            </span>
            <span className="text-[10px] text-amber-600 dark:text-amber-400">
              <span dangerouslySetInnerHTML={{ __html: t('instanceDetail.assignBanner.instructions') }} />
            </span>
          </div>
          <button
            type="button"
            onClick={() => onViewConfirmedAndSaved?.()}
            className="shrink-0 h-6 px-2.5 rounded-lg text-[10px] font-semibold text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-800/40 transition-colors"
          >
            ✕ {t('instanceDetail.assignBanner.cancel')}
          </button>
        </div>
      )}

      {/* ── View Manager Panel (hidden in read-only explainability viewers) ─────── */}
      {!readOnly && currentFrameMeta && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">

          {/* ── Compact single-row: AI chips + YOU chip + dropdown ── */}
          <div className="px-3 py-1.5 flex items-center flex-nowrap gap-1.5 flex-wrap min-h-[36px]">
            {(currentFrameMeta as { frame_id?: string; frame_number?: number })?.frame_id &&
              collaborationViewerTarget && (
              <CollaborationDiscussButton
                variant="compact"
                target={collaborationViewerTarget}
              />
            )}

            {/* AI label */}
            <span className="text-[9px] font-semibold text-purple-400 uppercase tracking-wide shrink-0">🤖</span>

            {/* AI chips */}
            {currentFrameMlViewDefIds.length > 0 ? (
              currentFrameMlViewDefIds.map((vid) => {
                const isActive = pendingViewDefId === vid;
                const isConfirmed = currentUserViewDefIds.includes(vid);
                return (
                  <button
                    key={vid}
                    type="button"
                    onClick={() => handleSelectPendingView(vid)}
                    title="Click to review this view's structures and validation"
                    className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium border transition-all ${isActive
                        ? 'bg-purple-500 border-purple-500 text-white shadow-sm'
                        : isConfirmed
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                      }`}
                  >
                    <Bot size={9} />
                    {viewDefDisplayName(vid)}
                    {isConfirmed && <span className="ml-0.5 text-blue-400">✓</span>}
                  </button>
                );
              })
            ) : (
              <span className="text-[10px] text-gray-400 italic">{t('instanceDetail.ml.noPredictionsYet')}</span>
            )}

            {/* ── Assign-mode chip: show the target view even if not yet AI-detected/confirmed ── */}
            {resolvedInitialViewDefId &&
              !currentFrameMlViewDefIds.includes(resolvedInitialViewDefId) &&
              !currentUserViewDefIds.includes(resolvedInitialViewDefId) && (
                <button
                  key={`assign-target-${resolvedInitialViewDefId}`}
                  type="button"
                  onClick={() => handleSelectPendingView(
                    pendingViewDefId === resolvedInitialViewDefId ? null : resolvedInitialViewDefId
                  )}
                  title="This is the view you are assigning — click to review required structures"
                  className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium border transition-all ${pendingViewDefId === resolvedInitialViewDefId
                      ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                      : 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                    }`}
                >
                  <span style={{ fontSize: 8 }}>🎯</span>
                  {viewDefDisplayName(resolvedInitialViewDefId)}
                </button>
              )}

            {/* Divider */}
            <span className="h-4 w-px bg-gray-200 dark:bg-gray-700 shrink-0 mx-0.5" />

            {/* YOU label */}
            <span className="text-[9px] font-semibold text-blue-400 uppercase tracking-wide shrink-0">👤</span>

            {/* All user-confirmed view chips (multi-view) */}
            {currentUserViewDefIds.length > 0 ? (
              currentUserViewDefIds.map((vid) => (
                <span
                  key={vid}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium bg-blue-50 border border-blue-300 text-blue-700"
                >
                  <Eye size={9} />
                  {viewDefDisplayName(vid)}
                  <button
                    type="button"
                    disabled={isAssigningView}
                    onClick={() => {
                      if (pendingViewDefId === vid) { setPendingViewDefId(null); setValidation(null); lastAutoValidatedKey.current = null; }
                      handleAssignView(vid, true);
                    }}
                    className="ml-0.5 text-gray-400 hover:text-red-500 font-bold leading-none disabled:opacity-40"
                    title={`Remove "${viewDefDisplayName(vid)}" from your confirmed views`}
                  >×</button>
                </span>
              ))
            ) : (
              <span className="text-[10px] text-gray-400 italic">{t('instanceDetail.user.notConfirmed')}</span>
            )}

            {/* Dropdown — manual assignment (shows all views not yet user-confirmed) */}
            <Select
              value=""
              disabled={isAssigningView || isSavingToReport}
              onChange={(e) => { if (e.target.value) handleSelectPendingView(Number(e.target.value)); }}
              className="h-6 w-[10rem] text-[10px] rounded-lg border border-dashed border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 text-gray-500 px-2 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer disabled:opacity-40"
              placeholder={t('instanceDetail.dropdown.assignView')}
              options={(filterOptions?.views ?? []).filter((v: any) => !currentUserViewDefIds.includes(v.id)).map((v: any) => ({
                value: String(v.id ?? ''),
                label: t(`viewTag.${v.view_code}`, { defaultValue: v.display_name }),
              }))}
            />

            {(isAssigningView || isValidating || isSavingToReport) && (
              <span className="w-3 h-3 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin shrink-0" />
            )}
          </div>

          {/* ── Validation Panel (shown when a view is being reviewed) ── */}
          {pendingViewDefId && (
            <div className={`border-t px-3 py-2 ${validation?.all_required_satisfied
                ? 'bg-green-50 dark:bg-green-950/20 border-green-100 dark:border-green-900'
                : 'bg-gray-50 dark:bg-gray-950 border-gray-100 dark:border-gray-800'
              }`}>
              {/* Header row */}
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 shrink-0">{t('instanceDetail.reviewing')}</span>
                  <span className="text-[10px] font-bold text-purple-700 dark:text-purple-400 truncate">{viewDefDisplayName(pendingViewDefId)}</span>
                  {/* Required progress badge */}
                  {!isValidating && validation?.checks.length ? (() => {
                    const required = validation.checks.filter((c: any) => c.required_for_match);
                    const satisfied = required.filter((c: any) => c.is_satisfied).length;
                    const total = required.length;
                    return (
                      <span className={`shrink-0 inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-bold ${satisfied === total
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                        {satisfied}/{total}
                      </span>
                    );
                  })() : null}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Confirm + Save to Report */}
                  <button
                    type="button"
                    disabled={isAssigningView || isValidating || isSavingToReport || !validation?.all_required_satisfied}
                    onClick={() => handleConfirmAndSave(pendingViewDefId)}
                    title={!validation?.all_required_satisfied ? 'Draw all required structures first' : 'Confirm view and add to report'}
                    className="h-6 px-2.5 rounded-lg text-[10px] font-semibold bg-green-500 border border-green-500 text-white hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {isSavingToReport ? (
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-white/40 border-t-white animate-spin" />{t('instanceDetail.actions.saving')}</span>
                    ) : t('instanceDetail.actions.confirmAndSave')}
                  </button>
                  {/* Assign only (no report) — shown when view not yet in user confirmed list */}
                  {!currentUserViewDefIds.includes(pendingViewDefId!) && (
                    <button
                      type="button"
                      disabled={isAssigningView || isSavingToReport}
                      onClick={() => handleAssignView(pendingViewDefId, false)}
                      title="Add to your confirmed views without saving to report"
                      className="h-6 px-2 rounded-lg text-[10px] font-medium border border-gray-200 bg-white dark:bg-gray-800 text-gray-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-40 transition-colors"
                    >
                      {t('instanceDetail.actions.assignOnly')}
                    </button>
                  )}
                  {/* Dismiss */}
                  <button
                    type="button"
                    onClick={() => { setPendingViewDefId(null); setValidation(null); }}
                    className="h-6 w-6 flex items-center justify-center rounded-lg text-[11px] border border-gray-200 text-gray-400 hover:text-red-400 hover:border-red-200 transition-colors"
                    title={t('instanceDetail.actions.close')}
                  >✕</button>
                </div>
              </div>

              {/* Structure checklist */}
              {isValidating ? (
                <div className="flex items-center gap-1.5 py-1">
                  <span className="w-3 h-3 rounded-full border-2 border-gray-200 border-t-purple-500 animate-spin" />
                  <p className="text-[10px] text-gray-400 italic">{t('instanceDetail.checkingStructures')}</p>
                </div>
              ) : validation?.checks.length ? (
                <>
                  <div className="flex flex-wrap gap-1">
                    {validation.checks.filter((c: any) => c.required_for_match).map((check: any) => {
                      const userCount = (check.all_segs ?? []).filter((s: any) => s.is_manual).length;
                      const aiCount = (check.all_segs ?? []).filter((s: any) => !s.is_manual).length;
                      const codeRaw = codeById?.[check.category_id] ?? check.display_name;
                      const codeNorm = normalizeQualFlagCode(codeRaw);
                      const checkLabel = codeNorm
                        ? t(`qualityFlag.${codeNorm}`, { defaultValue: check.display_name })
                        : check.display_name;
                      return (
                        <span
                          key={check.category_id}
                          className={`inline-flex items-center gap-1 h-6 px-2 rounded-lg text-[10px] font-medium border transition-colors ${check.is_satisfied
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : 'bg-white border-dashed border-orange-300 text-orange-600'
                            }`}
                        >
                          <span className={`text-[9px] font-bold ${check.is_satisfied ? 'text-green-500' : 'text-orange-400'}`}>
                            {check.is_satisfied ? '✓' : '○'}
                          </span>
                          {checkLabel}
                          {check.max_structures > 1 && <span className="opacity-50 text-[8px]">×{check.max_structures}</span>}
                          {userCount > 0 && <span title={t('instanceDetail.legend.userDrawingsTitle', { defaultValue: `${userCount} your drawing(s)`, count: userCount })} className="text-blue-500 text-[8px] font-semibold">👤{userCount > 1 ? userCount : ''}</span>}
                          {aiCount > 0 && <span title={t('instanceDetail.legend.aiDrawingsTitle', { defaultValue: `${aiCount} AI drawing(s)`, count: aiCount })} className="text-purple-500 text-[8px] font-semibold">🤖{aiCount > 1 ? aiCount : ''}</span>}
                        </span>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* ── Structure conflict panel — per biometry slot, not whole category ─── */}
      {structureConflicts.length > 0 && (
        <div className="flex-shrink-0 border-b border-orange-200 bg-orange-50 dark:bg-orange-950/30 px-3 py-2">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle size={13} className="text-orange-500 shrink-0" />
            <p className="text-xs font-semibold text-orange-600">
              {structureConflicts.length} structure conflict{structureConflicts.length > 1 ? 's' : ''} detected
              <span className="font-normal text-orange-500 ml-1">— both you and AI drew the same structure</span>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {structureConflicts.map(({ conflictKey, displayName, userSegs, mlSegs }) => {
              const userRefs = userSegs.map((seg) => ({
                seg_id: seg.id != null ? String(seg.id) : '',
                is_manual: true as const,
              })).filter((s) => s.seg_id !== '');
              const aiRefs = mlSegs.map((seg) => ({
                seg_id: seg.id != null ? String(seg.id) : '',
                is_manual: false as const,
              })).filter((s) => s.seg_id !== '');
              return (
                <div key={conflictKey} className="rounded-lg border border-orange-200 bg-white dark:bg-gray-900 p-2">
                  <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-200 mb-1.5 capitalize">
                    {displayName}
                  </p>
                  {/* Per-seg badges */}
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {userRefs.map(s => (
                      <span key={s.seg_id} className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-blue-100 border border-blue-200 text-blue-700 text-[9px] font-medium">
                        👤 {t('legend.yourDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.yourDrawing', { defaultValue: 'Your drawing' }) })}
                        <button type="button" onClick={() => handleResolveConflict(s.seg_id)} className="ml-0.5 text-red-400 hover:text-red-600 font-bold" title={t('legend.deleteYourDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.deleteYourDrawing', { defaultValue: 'Delete your drawing' }) })}>×</button>
                      </span>
                    ))}
                    {aiRefs.map(s => (
                      <span key={s.seg_id} className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-purple-100 border border-purple-200 text-purple-700 text-[9px] font-medium">
                        🤖 {t('legend.aiDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.aiDrawing', { defaultValue: 'AI drawing' }) })}
                        <button type="button" onClick={() => handleResolveConflict(s.seg_id)} className="ml-0.5 text-red-400 hover:text-red-600 font-bold" title={t('legend.deleteAiDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.deleteAiDrawing', { defaultValue: 'Delete AI drawing' }) })}>×</button>
                      </span>
                    ))}
                  </div>
                  {/* Batch resolution */}
                  <div className="flex gap-1.5 flex-wrap">
                    <button type="button" onClick={() => handleResolveConflict(aiRefs.map(s => s.seg_id))}
                      className="h-5 px-2 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[9px] font-semibold transition-colors">
                      Keep mine only
                    </button>
                    <button type="button" onClick={() => handleResolveConflict(userRefs.map(s => s.seg_id))}
                      className="h-5 px-2 rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 text-[9px] font-semibold transition-colors">
                      Use AI only
                    </button>
                    <span className="text-[9px] text-gray-400 self-center">or keep both as-is</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Viewport ─────────────────────────────────────────────────────────── */}
      <div
        ref={playbackContainerRef}
        className={`flex-1 min-h-0 relative overflow-hidden${isVideoInstance ? ' bg-black' : ' bg-gray-100 dark:bg-gray-950'}`}
      >
        {/* ── Frame view mode toggle — floats inside the viewport, never takes layout space ── */}
        {totalCount > 1 && (
          <div ref={gridPickerRef} className="absolute top-2 right-2 z-30 flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-md p-0.5">
            {/* Single-view button */}
            <button
              type="button"
              onClick={() => { setFrameViewMode('single'); setShowGridPicker(false); }}
              title="Single view"
              className={`flex items-center justify-center w-6 h-6 rounded transition-all ${frameViewMode === 'single' ? 'bg-white/20 text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`}
            >
              <LayoutList size={13} />
            </button>
            {/* Divider */}
            <span className="w-px h-4 bg-white/20 mx-0.5" />
            {/* Grid picker trigger */}
            <button
              type="button"
              onClick={() => setShowGridPicker(v => !v)}
              title="Choose grid layout"
              className={`flex items-center justify-center w-7 h-6 rounded transition-all gap-0.5 ${frameViewMode === 'grid' ? 'bg-white/20 text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`}
            >
              <GridLayoutIcon cols={frameViewMode === 'grid' ? gridSize : 3} rows={frameViewMode === 'grid' ? gridSize : 3} size={13} />
            </button>

            {/* ── Grid picker popover — hover NxN selector ── */}
            {showGridPicker && (
              <div ref={gridPickerRef} className="absolute top-full right-0 mt-1.5 z-50 bg-gray-900/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-3 w-44">
                <p className="text-[10px] font-semibold text-white/60 text-center mb-2 tracking-wide uppercase">Grid size</p>
                {/* 6×6 hover selector: hovering (row,col) selects max(row+1,col+1)×max(row+1,col+1) */}
                <div className="grid gap-[3px]" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}
                  onMouseLeave={() => setGridHoverN(0)}>
                  {Array.from({ length: 6 }, (_, row) =>
                    Array.from({ length: 6 }, (_, col) => {
                      const n = Math.max(row + 1, col + 1);
                      const activeN = frameViewMode === 'grid' ? gridSize : 0;
                      const highlighted = gridHoverN > 0 ? (row < gridHoverN && col < gridHoverN) : (row < activeN && col < activeN);
                      return (
                        <div
                          key={`${row}-${col}`}
                          onMouseEnter={() => setGridHoverN(n)}
                          onClick={() => { setGridSize(n); setGridPage(0); setFrameViewMode('grid'); setShowGridPicker(false); setGridHoverN(0); }}
                          className={`h-5 rounded-sm cursor-pointer transition-all border ${highlighted ? 'bg-blue-500 border-blue-400' : 'bg-white/10 border-white/15 hover:bg-white/20'}`}
                        />
                      );
                    })
                  )}
                </div>
                <p className="text-[10px] text-white/50 text-center mt-2">
                  {gridHoverN > 0
                    ? `${gridHoverN}×${gridHoverN} (${gridHoverN * gridHoverN} frames)`
                    : frameViewMode === 'grid' ? `${gridSize}×${gridSize} (current)` : 'Hover to select'}
                </p>
              </div>
            )}
          </div>
        )}
        {/* ── NxN grid — absolute inset-0 fully covers single view, wheel scrolls pages ── */}
        {frameViewMode === 'grid' && allFramesMeta.length > 0 && (() => {
          const perPage = gridSize * gridSize;
          const totalPages = Math.ceil(allFramesMeta.length / perPage);
          const safePage = Math.min(gridPage, totalPages - 1);
          const pageFrames = allFramesMeta.slice(safePage * perPage, (safePage + 1) * perPage);
          return (
            <div
              ref={gridContainerRef}
              className="absolute inset-0 p-1.5"
              style={{
                zIndex: 10,
                background: '#111827',          // solid — no transparency so single view is hidden
                display: 'grid',
                gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridSize}, minmax(0, 1fr))`,
                gap: 5,
              }}
              onWheel={e => {
                e.preventDefault();
                if (e.deltaY > 0) setGridPage(p => Math.min(totalPages - 1, p + 1));
                else setGridPage(p => Math.max(0, p - 1));
              }}
            >
              {pageFrames.map((meta: any) => {
                const globalIdx = allFramesMeta.indexOf(meta);
                const frameKey = `${meta.sop_instance_uid}_${meta.frame_number}`;
                const blobSrc = blobUrlsRef.current[frameKey] || null;
                const isActive = globalIdx === currentIdx - 1;
                const dims = gridImageDimsRef.current[frameKey];
                return (
                  <button
                    key={frameKey}
                    type="button"
                    data-frame-key={frameKey}
                    onClick={() => { goToFrameIndex(globalIdx); setFrameViewMode('single'); }}
                    className={`group relative focus:outline-none overflow-hidden transition-all rounded-lg ${isActive ? 'ring-2 ring-blue-500 ring-offset-1' : 'ring-1 ring-gray-700 hover:ring-blue-400'}`}
                    style={{ backgroundColor: '#111827', minHeight: 0 }}
                    title={`Frame ${globalIdx + 1}`}
                  >
                    {blobSrc ? (
                      <img
                        src={blobSrc}
                        alt={`Frame ${globalIdx + 1}`}
                        onLoad={(e) => {
                          const img = e.currentTarget;
                          const w = img.naturalWidth || 0;
                          const h = img.naturalHeight || 0;
                          if (w > 0 && h > 0) {
                            const prev = gridImageDimsRef.current[frameKey];
                            if (!prev || prev.w !== w || prev.h !== h) {
                              gridImageDimsRef.current[frameKey] = { w, h };
                              setGridOverlayVersion((v) => v + 1);
                            }
                          }
                        }}
                        className="absolute inset-0 w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-4 h-4 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
                      </div>
                    )}
                    {/* Grid segmentation overlay (read-only) */}
                    {blobSrc && dims && Array.isArray(meta.segmentations) && meta.segmentations.length > 0 && (
                      <svg
                        key={`${frameKey}::overlay::${gridOverlayVersion}`}
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox={`0 0 ${dims.w} ${dims.h}`}
                        preserveAspectRatio="xMidYMid meet"
                      >
                        {(() => {
                          const mk = new Set<string>();
                          for (const s of meta.segmentations) {
                            const g = (s.geometry_type ?? '').toUpperCase();
                            if ((g === 'ELLIPSE' || g === 'POLYLINE') && s.category_id != null &&
                              (s.segment_type === 'measurement' || s.label?.startsWith('MEAS__'))) {
                              mk.add(`${s.category_id}::${s.points}`);
                            }
                          }
                          return meta.segmentations.filter((s: any) => {
                            const g = (s.geometry_type ?? '').toUpperCase();
                            if ((g === 'ELLIPSE' || g === 'POLYLINE') && s.category_id != null &&
                              s.segment_type !== 'measurement' && !s.label?.startsWith('MEAS__') &&
                              mk.has(`${s.category_id}::${s.points}`)) return false;
                            return true;
                          });
                        })().map((seg: any, si: number) => {
                          const raw = convertSegmentationToRawShape(seg, frameKey);
                          if (!raw) return null;
                          let shape: any;
                          try { shape = rawShapeToShape(raw as any); } catch { return null; }
                          const stroke =
                            seg.category_id != null
                              ? getColorForCategoryId(seg.category_id)
                              : getColorForCode(seg.label ?? undefined);
                          const dashed = seg.is_manual === true ? undefined : '6 4';
                          const strokeWidth = 3;
                          if (shape.type === 'line') {
                            return (
                              <line
                                key={raw.id ?? si}
                                x1={shape.startPoint?.x}
                                y1={shape.startPoint?.y}
                                x2={shape.endPoint?.x}
                                y2={shape.endPoint?.y}
                                stroke={stroke}
                                strokeWidth={strokeWidth}
                                strokeDasharray={dashed}
                                fill="none"
                                opacity={0.95}
                              />
                            );
                          }
                          if (shape.type === 'square') {
                            const x = Math.min(shape.startPoint?.x ?? 0, shape.endPoint?.x ?? 0);
                            const y = Math.min(shape.startPoint?.y ?? 0, shape.endPoint?.y ?? 0);
                            const w = Math.abs((shape.endPoint?.x ?? 0) - (shape.startPoint?.x ?? 0));
                            const h = Math.abs((shape.endPoint?.y ?? 0) - (shape.startPoint?.y ?? 0));
                            return (
                              <rect
                                key={raw.id ?? si}
                                x={x}
                                y={y}
                                width={w}
                                height={h}
                                stroke={stroke}
                                strokeWidth={strokeWidth}
                                strokeDasharray={dashed}
                                fill="none"
                                opacity={0.95}
                              />
                            );
                          }
                          if (shape.type === 'circle') {
                            const rotDeg = shape.rotation ? (shape.rotation * 180) / Math.PI : 0;
                            return (
                              <ellipse
                                key={raw.id ?? si}
                                cx={shape.center?.x}
                                cy={shape.center?.y}
                                rx={shape.radiusX}
                                ry={shape.radiusY}
                                transform={rotDeg ? `rotate(${rotDeg} ${shape.center?.x} ${shape.center?.y})` : undefined}
                                stroke={stroke}
                                strokeWidth={strokeWidth}
                                strokeDasharray={dashed}
                                fill="none"
                                opacity={0.95}
                              />
                            );
                          }
                          if (shape.type === 'freehand') {
                            const pts = Array.isArray(shape.points)
                              ? shape.points.map((p: any) => `${p.x},${p.y}`).join(' ')
                              : '';
                            if (!pts) return null;
                            return (
                              <polyline
                                key={raw.id ?? si}
                                points={pts}
                                stroke={stroke}
                                strokeWidth={strokeWidth}
                                strokeDasharray={dashed}
                                fill="none"
                                opacity={0.95}
                              />
                            );
                          }
                          return null;
                        })}
                      </svg>
                    )}
                    <span className="absolute top-1 left-1 h-4 min-w-[16px] px-1 rounded text-white text-[8px] font-bold bg-black/60 flex items-center justify-center">
                      {globalIdx + 1}
                    </span>
                    {isActive && (
                      <div className="absolute inset-0 pointer-events-none bg-blue-500/20 flex items-end justify-center pb-1">
                        <span className="bg-blue-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">Active</span>
                      </div>
                    )}
                    {!isActive && (
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-1"
                        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}>
                        <span className="text-white text-[8px] font-semibold">Open</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* Single-frame view: ALWAYS rendered (not conditionally mounted) so DrawingCanvas
            stays alive in the background when grid is shown. Grid overlay sits on z-10 above. */}
        {true && <>
          {/* Vertical scrollbar — only visible in single mode */}
          {frameViewMode === 'single' && totalCount > 1 && (() => {
            const TRACK_PAD = 4;
            const THUMB_H = Math.max(24, Math.min(80, Math.round((1 / totalCount) * 300)));
            const fraction = totalCount > 1 ? (sliderIdx - 1) / (totalCount - 1) : 0;
            const calcTop = `calc(${TRACK_PAD}px + ${fraction} * (100% - ${TRACK_PAD * 2}px - ${THUMB_H}px))`;
            return (
              <div
                className="absolute right-0 top-0 bottom-0 z-20 select-none"
                style={{ width: 9, cursor: 'pointer', background: 'transparent' }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const usable = rect.height - TRACK_PAD * 2 - THUMB_H;
                  const relY = Math.max(0, Math.min(usable, e.clientY - rect.top - TRACK_PAD - THUMB_H / 2));
                  const idx = Math.round((relY / usable) * (totalCount - 1));
                  goToFrameIndex(idx);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const track = e.currentTarget;
                  const drag = (ev: MouseEvent) => {
                    const rect = track.getBoundingClientRect();
                    const usable = rect.height - TRACK_PAD * 2 - THUMB_H;
                    const relY = Math.max(0, Math.min(usable, ev.clientY - rect.top - TRACK_PAD - THUMB_H / 2));
                    const idx = Math.round((relY / usable) * (totalCount - 1));
                    goToFrameIndex(idx);
                  };
                  const up = () => {
                    window.removeEventListener('mousemove', drag);
                    window.removeEventListener('mouseup', up);
                  };
                  window.addEventListener('mousemove', drag);
                  window.addEventListener('mouseup', up);
                }}
              >
                <div
                  className="absolute left-0.5 right-0.5 rounded-sm pointer-events-none"
                  style={{
                    top: calcTop,
                    height: THUMB_H,
                    background: 'rgba(210,210,210,0.8)',
                  }}
                />
              </div>
            );
          })()}


          {/* Drawing legend — only visible in single mode */}
          {frameViewMode === 'single' && (
            <div className="absolute top-2 left-2 z-20 flex flex-col gap-0.5 bg-black/55 backdrop-blur-sm rounded-lg px-2 py-1.5 pointer-events-none select-none">
              <span className="flex items-center gap-1.5">
                <svg width="18" height="6" viewBox="0 0 18 6" fill="none"><line x1="0" y1="3" x2="18" y2="3" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" /></svg>
                <span className="text-[9px] text-blue-300 font-medium">{t('legend.yourDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.yourDrawing', { defaultValue: 'Your drawing' }) })}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="18" height="6" viewBox="0 0 18 6" fill="none"><line x1="0" y1="3" x2="18" y2="3" stroke="#c084fc" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="4 3" /></svg>
                <span className="text-[9px] text-purple-300 font-medium">{t('legend.aiDrawing', { ns: 'ViewDetails', defaultValue: t('instanceDetail.legend.aiDrawing', { defaultValue: 'AI drawing' }) })}</span>
              </span>
            </div>
          )}
          {isVideoInstance && videoStreamData?.url && (
            <video
              ref={videoElementRef}
              src={videoStreamData.url}
              // COEP require-corp is set on this app (webpack/nginx/_headers). Without crossOrigin,
              // cross-origin media is blocked (NotSameOriginAfterDefaultedToSameOriginByCoep). GCS
              // must still allow this origin in bucket CORS so decode can complete.
              crossOrigin="anonymous"
              preload="auto"
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                setVideoDimensions({ width: v.videoWidth, height: v.videoHeight });
              }}
              onLoadedData={() => {
                setVideoElementFailed(false);
                setVideoReady(true);
              }}
              onCanPlay={() => {
                setVideoElementFailed(false);
                setVideoReady(true);
              }}
              onError={() => {
                setVideoReady(false);
                setVideoElementFailed(true);
              }}
              className="absolute inset-x-0 top-0 w-full object-contain pointer-events-none"
              style={{ zIndex: 0, height: 'calc(100% - 48px)' }}
              muted
              playsInline
            />
          )}
          {isVideoInstance &&
            allFramesMeta.length > 0 &&
            !isMetaLoading &&
            (videoStreamFetchFailed || (videoStreamData && videoElementFailed)) ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm px-4 text-center">
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                {videoStreamFetchFailed
                  ? t('instanceDetail.video.urlUnavailable')
                  : t('instanceDetail.video.loadFailed')}
              </span>
            </div>
          ) : (isMetaLoading && imagesForDrawingCanvas.length === 0) ||
            (isVideoInstance && videoStreamData && !videoReady && !videoElementFailed && allFramesMeta.length > 0) ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 bg-white/80 dark:bg-gray-900/90 backdrop-blur-sm transition-colors duration-300">
              {/* Modern Spinner Design */}
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-[3px] border-blue-500/10 dark:border-blue-400/10" />
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 dark:border-t-blue-400 animate-spin" />
              </div>

              {isVideoInstance && videoStreamData && !videoReady && (
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs font-bold tracking-wider uppercase text-gray-500 dark:text-gray-400">
                    {t('instanceDetail.video.loading')}
                  </span>
                  {/* Optional: Subtle pulse dots for extra visual feedback */}
                  <div className="flex gap-1">
                    <div className="w-1 h-1 rounded-full bg-blue-500/40 animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-1 h-1 rounded-full bg-blue-500/40 animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-1 h-1 rounded-full bg-blue-500/40 animate-bounce" />
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* DrawingCanvas — always fully visible; for video, canvas draws only shapes over the visible <video>. */}
          <div className="h-full relative" style={{ zIndex: 1 }}>
            {imagesForDrawingCanvas.length > 0 && (
              <DrawingCanvas
                key={instance.sop_instance_uid}
                ref={drawingCanvasRef}
                images={imagesForDrawingCanvas}
                initialCurrentImageId={initialCurrentImageId}
                videoMode={isVideoInstance}
                videoDimensions={videoDimensions}
                syncShapesVersion={syncShapesVersion}
                onSave={handleSave}
                onImageChange={handleImageChange}
                showSaveButton={!readOnly}
                readOnly={readOnly}
                width={undefined}
                height={undefined}
                totalFrameCount={
                  externalNav ? externalNav.total : isVirtualized ? totalCount : undefined
                }
                globalCurrentFrameIndex={
                  externalNav ? externalNav.currentIndex + 1 : isVirtualized ? currentIdx : undefined
                }
                onGlobalFrameIndexChange={
                  externalNav
                    ? (oneBased) => externalNav.onNavigate(oneBased - 1)
                    : isVirtualized
                      ? (oneBased) => {
                          goToFrameIndex(oneBased - 1);
                        }
                      : undefined
                }
                playbackControl={
                  totalCount > 1
                    ? {
                      isPlaying,
                      onToggle: () => {
                        setIsPlaying((prev) => {
                          const next = !prev;
                          if (next) {
                            setCurrentImageId((prevId) => {
                              const meta = allFramesMetaRef.current;
                              if (!meta.length) return prevId;
                              const metaIdx = prevId != null
                                ? meta.findIndex((f: any) => `${f.sop_instance_uid}_${f.frame_number}` === prevId)
                                : -1;
                              if (metaIdx < 0 || metaIdx >= meta.length - 1) {
                                const first = meta[0];
                                return `${first.sop_instance_uid}_${first.frame_number}`;
                              }
                              return prevId;
                            });
                          }
                          return next;
                        });
                      },
                      disabled: false,
                      fps: playFps,
                      onFpsChange: setPlayFps,
                      /** When set (video mode), FPS options are limited to this value so playback stays compatible. */
                      maxFps: isVideoInstance && videoStreamData?.fps ? Math.round(videoStreamData.fps) : undefined,
                    }
                    : undefined
                }
              />
            )}
          </div>
        </>}
      </div>
    </div>
  );
}

/** Wrapped in memo so parent re-renders (e.g. thumbnail blob URL updates) don't cascade down. */
const GeneralViewerInstanceDetail = memo(GeneralViewerInstanceDetailInner);
export default GeneralViewerInstanceDetail;
